'use client';

import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useAuth, hasPerm } from '../lib/auth';
import { useLicense } from '../lib/license';
import { apiFetch, API_BASE } from '../lib/api';
import { APP_VERSION, APP_BUILD_DATE } from '../lib/version';
import { useTranslation, LANGUAGES, Locale, getOrderedLanguages } from '../lib/i18n';
import { useFormatLocale, getFormatLocale } from '../lib/useNationalEnvironment';
import { usePageTitleOverride } from '../lib/usePageTitle';
import { useScrollRestore } from '../lib/usePersistedState';
import { useOwners } from './OwnerSelector';
import { useExitAnimation } from '../lib/useExitAnimation';
import { useEmailCompose } from '../lib/emailCompose';
import { resolveCmsSiteSlug } from '../lib/cmsSites';
import { prefetchRoute } from '../lib/prefetch';
import { menuSearchKeywords, norm } from '../lib/pageSearch';
import { helpSlugForPath, helpLang, HelpPagesResponse } from '../lib/help';
import { isLoginRedirectHandled, setLoginRedirectHandled, isMustChangePassword } from '../lib/authStorage';
import { isPublicPath } from '../lib/publicPaths';
import { recordPathVisit } from '../lib/useReturnPath';
import { useNotificationPoll } from '../lib/useNotificationPoll';
import { useTheme } from '../lib/theme';
import { useFontSize, type FontSize, FONT_SIZE_PX } from '../lib/fontSize';
import { playBeep } from '../lib/sound';
import BackendHealth from './BackendHealth';
import ErrorBoundary from './ErrorBoundary';
import GlobalSearchModal from './GlobalSearchModal';
import HeaderSearch from './HeaderSearch';
import ImpersonationBanner from './ImpersonationBanner';
import LoadingBar, { notifyLoadingReset } from './LoadingBar';
import LoadingOverlay from './LoadingOverlay';
import ProfileModal from './ProfileModal';
import { EventModalProvider } from './EventModalProvider';
import { HelpDrawerProvider, useHelpDrawer } from './HelpDrawer';

type BackendInfo = {
  build?: {
    version?: string;
    time?: string;
  };
};

type LayoutProps = {
  children: ReactNode;
  /** Request běží na CMS hostu (NEXT_PUBLIC_CMS_HOSTS) — bez přihlášení jen veřejný web. */
  cmsHost?: boolean;
};

type SavedFilter = {
  id: number;
  label: string;
  ownerId: number;
  ownerName: string;
  periodId: number;
  periodName: string;
};

const MENU_ICONS: Record<string, string> = {
  '/calendar': '📅',
  '/danovy-kalendar': '🧾',
  '/tasks': '✅',
  '/meetings': '👥',
  '/calls': '📞',
  '/im': '🗨️',
  '/notes': '📝',
  '/news': '📰',
  '/email': '✉️',
  '/sms': '📱',
  '/in-chat': '💬',
  '/documents': '📁',
  '/chat': '�️',
  '/monitors': '📡',
  '/profile#google': '🔄',
  '/devices': '🖥️',
  '/payments': '💳',
  '/payment-requests': '🧾',
  '/create-payment': '⚡',
  '/credit': '💰',
  '/obchod/brands': '🏷️',
  '/brands/analytics': '📊',
  '/accounts': '🏢',
  '/contacts': '📇',
  '/obchod/analytics': '📈',
  '/obchod/invoices': '🧾',
  '/obchod/orders': '📜',
  '/obchod/shipments': '📮',
  '/obchod/exchange-rates': '💱',
  '/datovka': '📬',
  '/obchod/orders/status-legend': '🗺️',
  '/armorik': '📒',
  '/armorik/invoices': '🧾',
  '/armorik/orders': '📜',
  '/armorik/contracts': '📜',
  '/armorik/vat-rates': '📊',
  '/armorik/vat-report': '📊',
  '/armorik/chart-of-accounts': '🗂️',
  '/armorik/coa-templates': '📋',
  '/armorik/pre-accounting': '🔗',
  '/armorik/journal': '📔',
  '/armorik/owners': '👤',
  '/armorik/periods': '📅',
  '/armorik/folders': '📁',
  '/obchod/products': '📦',
  '/obchod/sklad': '🏬',
  '/obchod/price-actions': '🏷️',
  '/armorik/bank-accounts': '🏦',
  '/armorik/partner-bank-accounts': '🏦',
  '/armorik/bank-statements': '📃',
  '/armorik/number-sequences': '🔢',
  '/armorik/saldo-sheets': '📊',
  '/payroll': '💵',
  '/payroll/employees': '👥',
  '/payroll/periods': '📅',
  '/payroll/slips': '💰',
  '/payroll/components': '📋',
  '/payroll/absences': '🏖️',
  '/payroll/employments': '📝',
  '/payroll/deductions': '⚖️',
  '/payroll/filings': '📨',
  '/payroll/hr': '🧑‍⚕️',
  '/payroll/moje': '🙋',
  '/payroll/config': '⚙️',
  '/vincis/faktury': '📄',
  '/vincis/vykazy-fakturace': '📊',
  '/crm': '🎯',
  '/crm/leads': '🚀',
  '/crm/cases': '📋',
  '/crm/opportunities': '💡',
  '/crm/activities': '�',
  '/crm/supervision': '🛡️',
  '/crm/supervision/settings': '⚙️',
  '/crm/contact-sources': '🗂️',
  '/crm/campaigns': '📣',
  '/crm/contracts': '📜',
  '/crm/calendar': '🗓️',
  '/reservations': '📅',
  '/reservations/services': '🔧',
  '/admin': '⚙️',
  '/admin/payments': '🏦',
  '/admin/credit': '💵',
  '/admin/products': '🛠️',
  '/admin/licenses': '🎫',
  '/admin/datatable-filters': '🔎',
  '/admin/print-reports': '🖨️',
  '/admin/news': '📰',
  '/admin/saldo': '📊',
  '/admin/backend-health': '📡',
  '/admin/menu-order': '☰',
  '/admin/ooo-logs': '📨',
  '/admin/pohoda-users': '👥',
  '/consumption': '⚡',
  '/energetika/spot-ceny': '⚡',
  '/menu-order': '☰',
  '/help': '📚',
};

// Sekce pro tabbed menu mode. Každá položka patří do jedné sekce.
// Položky bez sekce (uložené filtry, /menu-order) se zobrazí v sekci "Ostatní".
type MenuSection = {
  id: string;
  icon: string;
  labelKey: string;
  /** hrefs patřící do této sekce */
  hrefs: Set<string>;
};

const MENU_SECTIONS: MenuSection[] = [
  {
    id: 'calendar',
    icon: '📅',
    labelKey: 'calendar',
    hrefs: new Set(['/calendar', '/tasks', '/meetings', '/calls', '/im', '/reservations', '/reservations/services']),
  },
  {
    id: 'comms',
    icon: '✉️',
    labelKey: 'menuComms',
    hrefs: new Set(['/email', '/sms', '/in-chat', '/chat', '/notes', '/news', '/datovka', '/documents']),
  },
  {
    id: 'stripe',
    icon: '💳',
    labelKey: 'menuStripe',
    hrefs: new Set(['/devices', '/payments', '/payment-requests', '/create-payment', '/credit']),
  },
  {
    id: 'obchod',
    icon: '📦',
    labelKey: 'menuObchod',
    hrefs: new Set([
      '/obchod/products', '/obchod/sklad', '/obchod/price-actions', '/obchod/brands', '/brands/analytics', '/obchod/analytics',
      '/accounts', '/contacts', '/obchod/invoices', '/obchod/orders',
      '/obchod/orders/status-legend', '/obchod/purchase-orders', '/obchod/shipments', '/obchod/exchange-rates',
    ]),
  },
  {
    id: 'energetika',
    icon: '⚡',
    labelKey: 'menuEnergetika',
    hrefs: new Set(['/energetika/spot-ceny']),
  },
  {
    id: 'armorik',
    icon: '📒',
    labelKey: 'menuArmorik',
    hrefs: new Set([
      '/armorik', '/armorik/owners', '/armorik/periods', '/armorik/vat-rates', '/armorik/vat-report',
      '/armorik/chart-of-accounts', '/armorik/coa-templates', '/armorik/pre-accounting',
      '/armorik/folders', '/armorik/bank-accounts', '/armorik/partner-bank-accounts',
      '/armorik/bank-statements', '/armorik/saldo-sheets', '/consumption',
    ]),
  },
  {
    id: 'vincis',
    icon: '📄',
    labelKey: 'menuVincisAgenda',
    hrefs: new Set(['/vincis']),
  },
  {
    id: 'crm',
    icon: '🎯',
    labelKey: 'menuCrmDashboard',
    hrefs: new Set(['/crm', '/monitors', '/crm/leads', '/crm/cases', '/crm/opportunities', '/crm/activities', '/crm/supervision', '/crm/supervision/settings', '/crm/contact-sources', '/crm/campaigns', '/crm/contracts', '/crm/calendar']),
  },
  {
    id: 'payroll',
    icon: '💵',
    labelKey: 'menuPayroll',
    hrefs: new Set(['/payroll', '/payroll/employees', '/payroll/periods', '/payroll/slips', '/payroll/components', '/payroll/config', '/payroll/absences', '/payroll/employments', '/payroll/deductions', '/payroll/filings', '/payroll/hr', '/payroll/moje']),
  },
  {
    id: 'admin',
    icon: '⚙️',
    labelKey: 'menuAdmin',
    hrefs: new Set([
      '/admin/payments', '/admin/stripe-logs', '/admin/credit', '/admin/products', '/admin/licenses',
      '/admin/entities', '/admin/view-configs', '/admin/datatable-filters',
      '/admin/news', '/admin/pohoda-users', '/admin/batch-jobs',
    ]),
  },
];

/** Najde sekci pro daný href. Vrací 'other' pokud nenajde. */
function findSectionId(href: string): string {
  for (const section of MENU_SECTIONS) {
    if (section.hrefs.has(href)) return section.id;
  }
  return 'other';
}

function formatRelativeTime(dateStr: string | null, t?: (key: string) => string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHod = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHod / 24);
  if (diffMin < 1) return t('compLayoutJustNow');
  if (diffMin < 60) return `${diffMin} min`;
  if (diffHod < 24) return `${diffHod} h`;
  if (diffDays === 1) return t('compLayoutYesterday');
  return `${diffDays} d`;
}

function formatEventDateTime(dateStr: string | null, t?: (key: string) => string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  const time = date.toLocaleTimeString(getFormatLocale(), { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `${t('today')} ${time}`;
  if (isTomorrow) return `${t('tomorrow')} ${time}`;
  return date.toLocaleDateString(getFormatLocale(), { day: 'numeric', month: 'numeric' }) + ` ${time}`;
}

function getEventIcon(eventType?: string | null, priority?: string | null): string {
  if (priority === 'HIGH' || priority === 'highest') return '🔴';
  if (eventType === 'TASK') return '📋';
  if (eventType === 'MEETING') return '👥';
  if (eventType === 'CALL') return '📞';
  if (eventType === 'IM') return '🗨️';
  if (eventType === 'DEADLINE') return '⏰';
  if (eventType === 'REMINDER') return '🔔';
  return '📅';
}

function getPriorityBorder(priority?: string | null): string {
  if (priority === 'HIGH' || priority === 'highest') return 'border-rust/50';
  if (priority === 'highest') return 'border-rust/50';
  return 'border-moss/30';
}

function getProgressToEvent(eventTime?: string | null, createdAt?: string | null): number | null {
  if (!eventTime || !createdAt) return null;
  const event = new Date(eventTime).getTime();
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  if (now >= event) return 100;
  const total = event - created;
  if (total <= 0) return null;
  const elapsed = now - created;
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

type NotificationItem = {
  id: number;
  title: string;
  body: string | null;
  type: string;
  refId: number | null;
  read: boolean;
  createdAt: string | null;
  eventTime?: string | null;
  eventEndTime?: string | null;
  dueDate?: string | null;
  eventType?: string | null;
  priority?: string | null;
  location?: string | null;
  snoozedUntil?: string | null;
};

type NotifKind = 'case_reply' | 'web_monitor' | 'calendar' | 'opportunity' | 'lead' | 'other';

function classifyNotif(type: string): NotifKind {
  if (type === 'CASE_REPLY') return 'case_reply';
  if (type === 'OPPORTUNITY') return 'opportunity';
  if (type === 'LEAD') return 'lead';
  if (type.startsWith('WEB_MONITOR_')) return 'web_monitor';
  if (type === 'CALENDAR_REMINDER') return 'calendar';
  return 'other';
}

function notifDisplayInfo(kind: NotifKind, type: string, t: (k: string) => string, eventType?: string | null, priority?: string | null) {
  if (kind === 'case_reply') {
    return { label: t('notifCaseReply'), icon: '🎫', openLabel: t('notifOpenCase') };
  }
  if (kind === 'opportunity') {
    return { label: t('notifOpportunity'), icon: '💼', openLabel: t('notifOpenOpportunity') };
  }
  if (kind === 'lead') {
    return { label: t('calendarLead'), icon: '🎯', openLabel: t('open') };
  }
  if (kind === 'web_monitor') {
    const sub = type.replace('WEB_MONITOR_', '');
    const icon = sub === 'DOWN' ? '🔴' : sub === 'UP' ? '🟢' : '🌐';
    const labelKey = sub === 'DOWN' ? 'notifWebMonitorDown' : sub === 'UP' ? 'notifWebMonitorUp' : 'notifWebMonitorChange';
    return { label: t(labelKey), icon, openLabel: t('notifOpenMonitor') };
  }
  if (kind === 'calendar') {
    return { label: t('notifCalendarReminder'), icon: getEventIcon(eventType, priority), openLabel: t('open') };
  }
  return { label: t('notifications'), icon: '🔔', openLabel: t('open') };
}

function notifOpenHref(kind: NotifKind, refId: number | null): string | null {
  if (!refId) return null;
  if (kind === 'case_reply') return `/crm/cases?case=${refId}`;
  if (kind === 'opportunity') return `/crm/opportunities/${refId}`;
  if (kind === 'lead') return `/crm/leads?lead=${refId}`;
  if (kind === 'web_monitor') return `/monitors/${refId}`;
  if (kind === 'calendar') return `/calendar?event=${refId}`;
  return null;
}

/** `?` v headeru — otevře článek nápovědy v postranním panelu (bez ztráty kontextu stránky). */
function HeaderHelpButton({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const drawer = useHelpDrawer();
  if (!drawer) return null;
  return (
    <button
      type="button"
      onClick={() => drawer.openHelp(slug)}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-ochre text-ochre text-sm font-bold hover:bg-ochre hover:text-cream transition focus:outline-none"
      title={t('menuNapoveda')}
      aria-label={t('menuNapoveda')}
    >
      ?
    </button>
  );
}

export default function Layout({ children, cmsHost }: LayoutProps) {
  const { locale, changeLocale, t, tLocale } = useTranslation();
  const fmtLocale = useFormatLocale();
  const router = useRouter();
  const pathname = usePathname();
  
  // Save & restore scroll position per page (survives idle-timeout re-login)
  useScrollRestore(pathname || 'unknown', true);
  
  const { isAuthenticated, email, role, permissions, firstName, lastName, mounted, idleWarning, idleSecondsLeft, isLoggingOut, logout: authLogout, dismissIdleWarning } = useAuth();
  // Payroll agenda: persistovaná účetní jednotka pro breadcrumb kontext
  const { owners: payrollOwners } = useOwners(mounted && pathname.startsWith('/payroll'));
  const [payrollUnitId, setPayrollUnitId] = useState<number | null>(null);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem('armorik:persist:payroll:ownerId');
      setPayrollUnitId(v != null ? (JSON.parse(v) as number | null) : null);
    } catch { /* ignore */ }
  }, [pathname]);
  useNotificationPoll();
  const { theme, toggleTheme } = useTheme();
  const { fontSize, setFontSize } = useFontSize();
  const [fontSizeOpen, setFontSizeOpen] = useState(false);
  const fontSizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (fontSizeRef.current && !fontSizeRef.current.contains(e.target as Node)) {
        setFontSizeOpen(false);
      }
    };
    if (fontSizeOpen) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [fontSizeOpen]);
  const { data: settings } = useSWR<{ site_name?: string; footer_text?: string }>(
    mounted && isAuthenticated ? (role === 'ADMIN' ? '/api/admin/settings' : '/api/settings/site-name') : null
  );
  const siteName = settings?.site_name || 'Armorik';
  // Kontextová nápověda — ? tlačítko se ukáže jen když článek pro aktuální
  // routu existuje v jazyce uživatele (žádný fallback).
  const helpLanguage = helpLang(locale);
  const { data: helpPages } = useSWR<HelpPagesResponse>(
    mounted && isAuthenticated ? `/api/user/help/${helpLanguage}/pages` : null
  );
  const helpSlugs = new Set<string>((helpPages?.pages || []).map((p) => p.slug));
  // Resolve numeric URL segments (record ids) to meaningful names in the header breadcrumb
  const CRUMB_RESOLVERS: Record<string, { url: (id: string) => string; label: (d: Record<string, unknown>) => string | null | undefined }> = {
    'armorik:owners': { url: (id) => `/api/user/armorik/accounting/owners/${id}`, label: (d) => (d.code as string) || (d.name as string) },
    'armorik:periods': { url: (id) => `/api/user/armorik/accounting/periods/${id}`, label: (d) => d.name as string },
    'armorik:folders': { url: (id) => `/api/user/armorik/accounting/folders/${id}`, label: (d) => d.name as string },
    'armorik:invoices': { url: (id) => `/api/user/armorik/accounting/invoices/${id}`, label: (d) => (d.invoiceNumber as string) || (d.name as string) },
    'armorik:orders': { url: (id) => `/api/user/armorik/accounting/orders/${id}`, label: (d) => (d.orderNumber as string) || (d.name as string) },
    'armorik:contracts': { url: (id) => `/api/user/armorik/accounting/contracts/${id}`, label: (d) => (d.contractNumber as string) || (d.name as string) },
    'armorik:saldo-sheets': { url: (id) => `/api/user/armorik/accounting/saldo-sheets/${id}`, label: (d) => d.name as string },
    'armorik:chart-of-accounts': { url: (id) => `/api/user/armorik/accounting/chart-of-accounts/${id}`, label: (d) => (d.code as string) || (d.name as string) },
    'accounts': { url: (id) => `/api/user/obchod/accounts/${id}`, label: (d) => (d.name as string) || (d.companyName as string) },
    'obchod:accounts': { url: (id) => `/api/user/obchod/accounts/${id}`, label: (d) => (d.name as string) || (d.companyName as string) },
    'obchod:products': { url: (id) => `/api/user/obchod/products/${id}`, label: (d) => d.name as string },
    'obchod:orders': { url: (id) => `/api/user/obchod/orders/${id}`, label: (d) => (d.number as string) || (d.cislo as string) || (d.name as string) },
    'obchod:invoices': { url: (id) => `/api/user/obchod/invoices/${id}`, label: (d) => (d.number as string) || (d.cislo as string) || (d.name as string) },
    'crm:leads': { url: (id) => `/api/user/crm/leads/${id}`, label: (d) => (d.name as string) || (d.title as string) || ([d.firstName, d.lastName].filter(Boolean).join(' ') || undefined) || (d.company as string) },
    'devices': { url: (id) => `/api/user/devices/${id}`, label: (d) => (d.name as string) || (d.hostname as string) || (d.serialNumber as string) },
    'admin:entities': { url: (id) => `/api/admin/dynamic-entities/${id}`, label: (d) => (d.displayName as string) || (d.name as string) },
    'crm:opportunities': { url: (id) => `/api/user/crm/opportunities/${id}`, label: (d) => (d.name as string) || (d.title as string) },
    'crm:cases': { url: (id) => `/api/user/crm/cases/${id}`, label: (d) => (d.name as string) || (d.title as string) },
    'admin:users': { url: (id) => `/api/admin/users/${id}`, label: (d) => (d.email as string) || (d.username as string) || (d.name as string) },
    'admin:entity-views': { url: (id) => `/api/admin/entity-views/${id}`, label: (d) => d.name as string },
  };
  const { data: bcResolved } = useSWR<Record<number, string>>(
    mounted && isAuthenticated && pathname ? ['bc-resolve', pathname] : null,
    async () => {
      const segs = pathname.split('/').filter(Boolean);
      const out: Record<number, string> = {};
      await Promise.all(segs.map(async (seg, i) => {
        if (i === 0 || !/^\d+$/.test(seg)) return;
        const prev = segs[i - 1];
        const key = segs[0] === prev ? prev : `${segs[0]}:${prev}`;
        const r = CRUMB_RESOLVERS[key];
        if (!r) return;
        try {
          const d = await apiFetch<Record<string, unknown>>(r.url(seg));
          const l = r.label(d);
          if (l) out[i] = String(l);
        } catch { /* keep raw segment on failure */ }
      }));
      return out;
    },
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const mobileSidebarExiting = useExitAnimation(mobileSidebarOpen);
  const [sidebarTooltip, setSidebarTooltip] = useState<{ label: string; top: number; href?: string } | null>(null);
  const tooltipHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSidebarTooltip = (tooltip: { label: string; top: number; href?: string }) => {
    if (tooltipHideTimer.current) { clearTimeout(tooltipHideTimer.current); tooltipHideTimer.current = null; }
    setSidebarTooltip(tooltip);
  };
  const hideSidebarTooltip = () => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    tooltipHideTimer.current = setTimeout(() => setSidebarTooltip(null), 300);
  };
  // Menu mode: 'flat' (ploché), 'sections' (sbalitelné sekce), 'dynamic' (často použité + sekce)
  // Default: 'dynamic' — uživatelé si mohou přepnout na flat/sections
  const [menuMode, setMenuMode] = useState<'flat' | 'sections' | 'dynamic'>('dynamic');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [pinnedHrefs, setPinnedHrefs] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedMode = localStorage.getItem('menuMode');
    if (savedMode === 'sections' || savedMode === 'flat' || savedMode === 'dynamic') {
      setMenuMode(savedMode);
    }
    const savedCollapsed = localStorage.getItem('menuCollapsedSections');
    if (savedCollapsed) {
      try { setCollapsedSections(new Set(JSON.parse(savedCollapsed))); } catch { /* tiché */ }
    }
    const savedPinned = localStorage.getItem('menuPinned');
    if (savedPinned) {
      try { setPinnedHrefs(new Set(JSON.parse(savedPinned))); } catch { /* tiché */ }
    }
  }, []);
  const togglePin = (href: string) => {
    setPinnedHrefs(prev => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      try { localStorage.setItem('menuPinned', JSON.stringify(Array.from(next))); } catch { /* tiché */ }
      return next;
    });
  };
  const cycleMenuMode = () => {
    const order: ('flat' | 'sections' | 'dynamic')[] = ['flat', 'sections', 'dynamic'];
    const idx = order.indexOf(menuMode);
    const next = order[(idx + 1) % order.length];
    setMenuMode(next);
    try { localStorage.setItem('menuMode', next); } catch { /* tiché */ }
  };
  const menuModeIcon = menuMode === 'flat' ? '▦' : menuMode === 'sections' ? '▤' : '★';
  const menuModeTitle = menuMode === 'flat' ? (t('menuModeSections'))
    : menuMode === 'sections' ? (t('menuModeDynamic'))
    : (t('menuModeFlat'));
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      try { localStorage.setItem('menuCollapsedSections', JSON.stringify(Array.from(next))); } catch { /* tiché */ }
      return next;
    });
  };
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [buildDateLabel, setBuildDateLabel] = useState<string | null>(null);
  const [menuSearch, setMenuSearch] = useState('');
  const [menuSearchOpen, setMenuSearchOpen] = useState(false);
  const [menuSearchActiveIndex, setMenuSearchActiveIndex] = useState(0);
  const [sloganVariant, setSloganVariant] = useState(0);
  useEffect(() => { setSloganVariant(Math.floor(Math.random() * 1000)); }, []);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [openChoice, setOpenChoice] = useState<{ href: string; label: string } | null>(null);
  const openChoiceExiting = useExitAnimation(openChoice != null);
  const idleWarningExiting = useExitAnimation(idleWarning);
  // Při exit animaci držet poslední openChoice, aby dialog měl co vykreslit
  const [lastOpenChoice, setLastOpenChoice] = useState(openChoice);
  if (openChoice && openChoice !== lastOpenChoice) setLastOpenChoice(openChoice);
  const choice = openChoice ?? lastOpenChoice;
  const composeEmail = useEmailCompose();
  const menuSearchRef = useRef<HTMLDivElement>(null);
  const menuSearchInputRef = useRef<HTMLInputElement>(null);
  const menuSearchItemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const { data: backendInfo } = useSWR<BackendInfo>(
    `${API_BASE}/actuator/info`,
    (url: string) => fetch(url).then(r => r.json()),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );
  const recordNameOverride = usePageTitleOverride();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const createMenuRef = useRef<HTMLDivElement>(null);

  // Combined unread counts — single request instead of 3 separate polls
  const { data: allUnread, mutate: mutateUnread } = useSWR<{ notifications: number; chat: number; inChat: number; databox: number; reservationsPending: number }>(
    mounted && isAuthenticated ? '/api/user/notifications/all-unread-counts' : null,
    (url: string) => apiFetch<{ notifications: number; chat: number; inChat: number; databox: number; reservationsPending: number }>(url, { silent: true }),
    { revalidateOnFocus: false, revalidateOnReconnect: true, refreshInterval: 30000, refreshWhenHidden: false }
  );
  const unreadCount = allUnread ? { count: allUnread.notifications } : undefined;
  const chatUnreadData = allUnread ? { unreadCount: allUnread.chat } : undefined;
  const inChatUnread = allUnread ? { unreadCount: allUnread.inChat } : undefined;
  const databoxUnread = allUnread?.databox ?? 0;
  const reservationsPending = allUnread?.reservationsPending ?? 0;

  // Notifications SWR (needed by markAsRead and dropdown)
  const { data: notifications, mutate: mutateNotifs } = useSWR<NotificationItem[]>(
    mounted && isAuthenticated && notifOpen ? '/api/user/notifications' : null
  );

  const markAsRead = useCallback(async (id: number) => {
    await apiFetch(`/api/user/notifications/${id}/read`, { method: 'PUT' });
    mutateNotifs();
    mutateUnread();
  }, [mutateNotifs, mutateUnread]);

  const markAllAsRead = useCallback(async () => {
    await apiFetch('/api/user/notifications/read-all', { method: 'PUT' });
    mutateNotifs();
    mutateUnread();
  }, [mutateNotifs, mutateUnread]);

  // Stacked banners — multiple notifications shown simultaneously
  const [bannerNotifs, setBannerNotifs] = useState<NotificationItem[]>([]);
  const [snoozedNotifs, setSnoozedNotifs] = useState<Set<number>>(new Set());
  const snoozeTimersRef = useRef<Map<number, number>>(new Map());
  const lastSoundTimeRef = useRef(0);
  const shownBannerIdsRef = useRef<Set<number>>(new Set());

  // Cross-tab sync via BroadcastChannel
  const notifChannelRef = useRef<BroadcastChannel | null>(null);

  // Admin-vynucená změna hesla: přesměruj na profil dokud flag trvá
  useEffect(() => {
    if (!mounted || !isAuthenticated) return;
    if (pathname === '/profile') return;
    if (isMustChangePassword()) {
      router.push('/profile#password');
    }
  }, [mounted, isAuthenticated, pathname, router]);

  useEffect(() => {
    if (!mounted || !isAuthenticated) return;
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('notif-sync');
    notifChannelRef.current = channel;
    channel.onmessage = (e: MessageEvent) => {
      const { type, notifId } = e.data;
      if (type === 'dismiss' || type === 'complete' || type === 'read') {
        setBannerNotifs(prev => prev.filter(n => n.id !== notifId));
      } else if (type === 'snooze') {
        setBannerNotifs(prev => prev.filter(n => n.id !== notifId));
        setSnoozedNotifs(prev => new Set(prev).add(notifId));
      }
    };
    return () => {
      channel.close();
      notifChannelRef.current = null;
    };
  }, [mounted, isAuthenticated]);

  const broadcastNotifAction = useCallback((type: 'dismiss' | 'complete' | 'read' | 'snooze', notifId: number) => {
    notifChannelRef.current?.postMessage({ type, notifId });
  }, []);

  // Request notification permission on mount — removed (using in-app notifications only)

  const dismissBanner = useCallback((id: number) => {
    setBannerNotifs(prev => prev.filter(n => n.id !== id));
    broadcastNotifAction('dismiss', id);
  }, [broadcastNotifAction]);

  const readAndDismiss = useCallback(async (id: number) => {
    try {
      await apiFetch(`/api/user/notifications/${id}/read`, { method: 'PUT' });
    } catch (err) {
      console.error('Failed to mark notification as read', id, err);
    }
    setBannerNotifs(prev => prev.filter(n => n.id !== id));
    broadcastNotifAction('read', id);
    mutateNotifs();
    mutateUnread();
  }, [mutateNotifs, mutateUnread, broadcastNotifAction]);

  const completeAndDismiss = useCallback(async (notifId: number, refId: number | null) => {
    if (refId) {
      try {
        await apiFetch(`/api/user/calendar/events/${refId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'COMPLETED' }),
        });
      } catch (err) {
        console.error('Failed to complete event', refId, err);
      }
    }
    try {
      await apiFetch(`/api/user/notifications/${notifId}/read`, { method: 'PUT' });
    } catch (err) {
      console.error('Failed to mark notification as read', notifId, err);
    }
    setBannerNotifs(prev => prev.filter(n => n.id !== notifId));
    broadcastNotifAction('complete', notifId);
    mutateNotifs();
    mutateUnread();
  }, [mutateNotifs, mutateUnread, broadcastNotifAction]);

  const handleNewNotification = useCallback((notif: NotificationItem) => {
    // Transient events share the same SSE stream (NEW_EMAIL, EMAIL_FOLDER_CHANGED)
    // but aren't persisted bell notifications — no numeric id/read flag.
    // Without this guard they render as an empty "Připomenutí události" banner.
    if (typeof notif.id !== 'number' || notif.read == null) return;
    // Skip already-read notifications (e.g. from SSE catch-up)
    if (notif.read) return;

    // Update unread count
    mutateUnread();

    // Check if we should show banner (not already shown in this session)
    if (shownBannerIdsRef.current.has(notif.id)) return;

    // For Web Monitor notifications: skip banner if older than 10 minutes
    // (SSE catch-up re-sends all unread on reconnect — don't spam old monitor alerts)
    if (notif.type.startsWith('WEB_MONITOR_') && notif.createdAt) {
      const ageMs = Date.now() - new Date(notif.createdAt).getTime();
      if (ageMs > 10 * 60 * 1000) {
        shownBannerIdsRef.current.add(notif.id);
        return;
      }
    }

    // Debounce sound — max 1x per 10 seconds
    const now = Date.now();
    if (now - lastSoundTimeRef.current > 10000) {
      lastSoundTimeRef.current = now;
      playBeep();
    }

    // Add to banner stack (max 5), replacing any existing banner for the same event (refId)
    shownBannerIdsRef.current.add(notif.id);
    setBannerNotifs(prev => {
      const filtered = notif.refId != null
        ? prev.filter(n => n.refId !== notif.refId)
        : prev.filter(n => n.id !== notif.id);
      return [...filtered, notif].slice(-5);
    });

    // Browser push notification removed — using in-app notifications only
  }, [mutateUnread, router, markAsRead]);

  // SSE catch-up now handles showing missed notifications on connect — no need for separate page-load fetch

  // SSE connection for real-time notifications (using EventSource for reliable SSE delivery)
  useEffect(() => {
    if (!mounted || !isAuthenticated) return;
    const email = typeof window !== 'undefined' ? window.localStorage.getItem('email') : null;
    if (!email) return;

    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;

    const connect = () => {
      es = new EventSource(`${API_BASE}/api/user/notifications/stream`, { withCredentials: true });
      es.addEventListener('notification', (event) => {
        try {
          const notif = JSON.parse((event as MessageEvent).data) as NotificationItem;
          handleNewNotification(notif);
          // Broadcast to other components (e.g. email page) so they don't need
          // their own SSE connection — each tab must hold as few HTTP/1.1
          // connections on api.* as possible (browser limit is ~6).
          window.dispatchEvent(new CustomEvent('armorik:notification', { detail: notif }));
        } catch {}
      });
      es.addEventListener('open', () => {
        reconnectAttempts = 0; // reset backoff on successful connection
      });
      es.onerror = () => {
        es?.close();
        es = null;
        // EventSource doesn't auto-reconnect with credentials, so do it manually.
        // Exponential backoff: 5s, 10s, 20s, 40s, 60s (cap) to avoid hammering broken SSE
        if (!reconnectTimer) {
          const delay = Math.min(5000 * Math.pow(2, reconnectAttempts), 60000);
          reconnectAttempts++;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect();
          }, delay);
        }
      };
    };

    connect();

    return () => {
      es?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [mounted, isAuthenticated, handleNewNotification]);

  const snoozeNotif = useCallback(async (notif: NotificationItem, minutes: number) => {
    setSnoozedNotifs(prev => new Set(prev).add(notif.id));
    setBannerNotifs(prev => prev.filter(n => n.id !== notif.id));
    broadcastNotifAction('snooze', notif.id);
    // Mark the notification as read so SSE catch-up doesn't re-send it on
    // page refresh / new tab. The snooze creates a new reminder which will
    // produce a fresh notification when it fires.
    try {
      await apiFetch(`/api/user/notifications/${notif.id}/read`, { method: 'PUT' });
      mutateNotifs();
      mutateUnread();
    } catch (err) {
      console.error('Failed to mark notification as read during snooze', notif.id, err);
    }
    // Persist snooze on the event via backend
    if (notif.refId) {
      try {
        await apiFetch(`/api/user/calendar/events/${notif.refId}/snooze`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ minutes }),
        });
      } catch (err) {
        console.error('Failed to snooze event', notif.refId, err);
      }
    }
    // Clear any existing timer for this notif
    const existing = snoozeTimersRef.current.get(notif.id);
    if (existing) clearTimeout(existing);
    // Set new timer to re-show after snooze period (in case SSE doesn't
    // deliver the new reminder notification in time, e.g. tab stays open)
    const timer = window.setTimeout(() => {
      setSnoozedNotifs(prev => {
        const next = new Set(prev);
        next.delete(notif.id);
        return next;
      });
      setBannerNotifs(prev => {
        if (prev.some(n => n.id === notif.id)) return prev;
        return [...prev, notif].slice(-5);
      });
    }, minutes * 60 * 1000);
    snoozeTimersRef.current.set(notif.id, timer);
  }, [broadcastNotifAction, mutateNotifs, mutateUnread]);

  // Cleanup snooze timers on unmount
  useEffect(() => {
    return () => {
      snoozeTimersRef.current.forEach(t => clearTimeout(t));
      snoozeTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!notifOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  useEffect(() => {
    if (APP_BUILD_DATE) {
      setBuildDateLabel(new Date(APP_BUILD_DATE).toLocaleString(getFormatLocale()));
    }
  }, []);

  const visibleNavItems = [
    { href: '/register', label: t('register'), authRequired: false, hideWhenAuth: true },
    { href: '/login', label: t('login'), authRequired: false, hideWhenAuth: true },
  ].filter((item) => {
    if (item.authRequired && !isAuthenticated) return false;
    if (item.hideWhenAuth && isAuthenticated) return false;
    return true;
  });

  // Načtení uložených filtrů pro rychlý přístup k vlastníkům/obdobím
  const { data: savedFilters } = useSWR<SavedFilter[]>(
    mounted && isAuthenticated ? '/api/user/armorik/accounting/saved-filters' : null
  );

  const cmsSiteSlug = mounted && typeof window !== 'undefined' ? resolveCmsSiteSlug(window.location.host) : null;
  const dropdownItems = [] as { href: string; label: string; key?: string; keywords?: string[] }[];
  // Helper pro položky s překladovým klíčem — ukládá klíč kvůli hledání v menu,
  // které matchuje jak aktuální jazyk, tak angličtinu (ENG fallback).
  // Volitelná keywords přidávají aliasy pro vyhledávání (např. "support" pro cases).
  const mi = (href: string, key: string, keywords?: string[]) => dropdownItems.push({ href, label: t(key), key, keywords: keywords ?? menuSearchKeywords(href) });

  // Kalendář a události
  if (mounted && (role === 'ADMIN' || permissions.includes('calendar.view'))) {
    mi('/calendar', 'calendar');
    mi('/tasks', 'tasks');
    mi('/meetings', 'meetings');
    mi('/calls', 'calls');
    mi('/im', 'imMessages');
  }

  // Daňový kalendář
  if (mounted && (role === 'ADMIN' || permissions.includes('tax-calendar.view'))) {
    mi('/danovy-kalendar', 'menuTaxCalendar', ['dane', 'danovy', 'tax', 'splatnost']);
  }

  // Rezervace
  if (mounted && (role === 'ADMIN' || permissions.includes('reservations.view'))) {
    mi('/reservations', 'menuReservations');
    mi('/reservations/services', 'menuReservationServices');
  }

  // Spotřeba (Consumption)
  if (mounted && (role === 'ADMIN' || permissions.includes('consumption.view'))) {
    mi('/consumption', 'menuConsumption');
  }

  // Email
  if (mounted && (role === 'ADMIN' || permissions.includes('email.view'))) {
    mi('/email', 'email');
  }

  // SMS
  if (mounted && isAuthenticated && (role === 'ADMIN' || permissions.includes('sms.view'))) {
    mi('/sms', 'menuSms');
  }

  // InChat
  if (mounted && isAuthenticated) {
    mi('/in-chat', 'menuInChat');
  }

  // Chat
  if (mounted && (role === 'ADMIN' || permissions.includes('chat.view'))) {
    mi('/chat', 'menuChat');
  }

  // Dokumenty
  if (mounted && (role === 'ADMIN' || permissions.includes('documents.view'))) {
    mi('/documents', 'menuDocuments');
  }

  // Datová schránka (ISDS)
  if (mounted && (role === 'ADMIN' || permissions.includes('databox.view') || permissions.includes('databox.edit'))) {
    mi('/datovka', 'menuDatovka');
  }

  // Stripe sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.terminals.view'))) {
    mi('/devices', 'terminals');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.payments-history.view'))) {
    mi('/payments', 'payments');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.payment-requests.view'))) {
    mi('/payment-requests', 'paymentRequests');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.create-payment.view'))) {
    mi('/create-payment', 'quickPayment');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('user.credit.view'))) {
    mi('/credit', 'credit');
  }

  // Pohoda sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.products.view'))) {
    mi('/obchod/products', 'adminProductsPohoda');
    mi('/obchod/sklad', 'menuSklad');
    mi('/obchod/price-actions', 'menuPriceActions');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.brands.view'))) {
    mi('/obchod/brands', 'menuBrandsPohoda');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.analytics.view'))) {
    mi('/brands/analytics', 'menuBrandAnalytics');
    mi('/obchod/analytics', 'menuSalesAnalytics');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('pohoda.accounts.view'))) {
    mi('/accounts', 'adminAccountsPohoda');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.ad-groups.edit'))) {
    mi('/obchod/ad-groups', 'menuAdGroups');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.contacts.view'))) {
    mi('/contacts', 'menuContacts');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.invoices.view'))) {
    mi('/obchod/invoices', 'adminInvoicesPohoda');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.orders.view') || permissions.includes('obchod.shipments.view'))) {
    mi('/obchod/orders', 'adminOrdersPohoda');
    mi('/obchod/shipments', 'menuShipments');
    mi('/obchod/orders/status-legend', 'menuLogisticsStatuses');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.products.view') || permissions.includes('obchod.invoices.view') || permissions.includes('obchod.orders.view'))) {
    mi('/obchod/exchange-rates', 'menuExchangeRates');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.purchase-orders.view'))) {
    mi('/obchod/purchase-orders', 'menuPurchaseOrders');
  }

  // Energetika sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('energetika.spot-prices.view'))) {
    mi('/energetika/spot-ceny', 'menuSpotPrices');
  }

  // Armorik účetnictví sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('armorik.accounting.view'))) {
    mi('/armorik', 'menuArmorikSelectOwner');
    mi('/armorik/owners', 'menuArmorikOwners');
    mi('/armorik/periods', 'menuArmorikPeriods');
    mi('/armorik/vat-rates', 'menuVatRates');
    mi('/armorik/vat-report', 'menuVatReport');
    mi('/armorik/chart-of-accounts', 'menuChartOfAccounts');
    mi('/armorik/coa-templates', 'menuCoaTemplates');
    mi('/armorik/pre-accounting', 'menuPreAccounting');
    mi('/armorik/folders', 'menuFolders');
    mi('/armorik/number-sequences', 'armorikTileNumberSequences');
    mi('/armorik/bank-accounts', 'menuOurBankAccounts');
    mi('/armorik/partner-bank-accounts', 'menuPartnerBankAccounts');
    mi('/armorik/bank-statements', 'menuBankStatements');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('armorik.saldo.view'))) {
    mi('/armorik/saldo-sheets', 'menuSaldo');
  }

  // Uložené filtry (vlastník/období) — rychlý přístup do menu (bez překladového klíče)
  if (savedFilters && savedFilters.length > 0) {
    for (const filter of savedFilters) {
      dropdownItems.push({
        href: `/armorik/owners/${filter.ownerId}/periods/${filter.periodId}`,
        label: `🔖 ${filter.label}`,
      });
    }
  }

  // Vincis sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('vincis.faktury.view') || permissions.includes('vincis.vykazy.view'))) {
    mi('/vincis', 'menuVincisAgenda');
  }

  // CRM sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('crm.view'))) {
    mi('/crm', 'menuCrmDashboard');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('monitors.view'))) {
    mi('/monitors', 'menuMonitors');
  }
  if (mounted && isAuthenticated) {
    mi('/notes', 'menuNotes');
  }
  if (mounted && isAuthenticated) {
    mi('/news', 'menuNews');
  }
  if (mounted && isAuthenticated) {
    mi('/links', 'menuLinks');
  }
  if (mounted && isAuthenticated) {
    mi('/help', 'menuNapoveda');
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.leads.view')) {
    mi('/crm/leads', 'menuCrmLeads');
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.cases.view')) {
    mi('/crm/cases', 'menuCrmCases', ['support']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.opportunities.view')) {
    mi('/crm/opportunities', 'menuCrmOpportunities');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('calendar.view'))) {
    mi('/crm/activities', 'menuCrmActivities');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('crm.supervise'))) {
    mi('/crm/supervision', 'menuCrmSupervision', ['dohled']);
    mi('/crm/supervision/settings', 'menuImpulseSettings', ['impuls']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.campaigns.view')) {
    mi('/crm/campaigns', 'menuCrmCampaigns', ['kampan', 'campaign']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.contracts.view')) {
    mi('/crm/contracts', 'menuCrmContracts', ['smlouv', 'contract']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.quotes.view')) {
    mi('/crm/quotes', 'menuCrmQuotes', ['nabidk', 'quote']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.view', 'crm.calendar.view')) {
    mi('/crm/calendar', 'menuCrmCalendar', ['kalendr', 'calendar']);
  }
  if (mounted && hasPerm(role, permissions, 'crm.contact-sources.edit')) {
    mi('/crm/contact-sources', 'menuCrmContactSources', ['zdroj', 'source']);
  }

  // Mzdy jsou zapuštěné v účetní jednotce — žádná položka hlavního menu;
  // vstup pouze přes /armorik/owners/{id} → Mzdy (nese ownerId v URL).

  // Admin sekce
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.payments.view'))) {
    mi('/admin/payments', 'adminPayments');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.payments.view'))) {
    mi('/admin/stripe-logs', 'adminStripeLogs');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('stripe.credit.view'))) {
    mi('/admin/credit', 'adminCreditOverview');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('admin.products.view'))) {
    mi('/admin/products', 'adminProducts');
  }
  if (mounted && cmsSiteSlug) {
    mi(`/${cmsSiteSlug}`, 'menuWebPreview');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('admin.users.view'))) {
    mi('/admin/licenses', 'adminLicenses');
  }
  if (mounted && role === 'ADMIN') {
    mi('/admin/entities', 'menuEntities');
    mi('/admin/view-configs', 'menuViewConfigs');
    mi('/admin/datatable-filters', 'menuDatatableFilters');
    mi('/admin/i18n-modules', 'i18nModulesTitle');
    mi('/admin/news', 'adminNewsTitle');
    mi('/admin/backend-health', 'adminBackendHealthTitle');
    mi('/admin/pohoda-users', 'pohodaUsersTitle');
    mi('/admin/cms/sites', 'menuCmsSites');
    mi('/admin/cms/pages', 'menuCmsPages');
    mi('/admin/cms/templates', 'menuCmsTemplates');
    mi('/admin/cms/blocks', 'menuCmsBlocks');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('admin.users.view'))) {
    mi('/admin/batch-jobs', 'menuBatchJobs');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('pohoda.accounts.duplicates'))) {
    mi('/admin/merge-log', 'menuMergeLog');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('obchod.contacts.edit'))) {
    mi('/admin/email-privacy', 'menuEmailPrivacy');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('eshop.manage'))) {
    mi('/admin/eshop', 'menuEshop');
  }
  if (mounted && (role === 'ADMIN' || permissions.includes('cms.edit'))) {
    mi('/cms/editor', 'menuWebEditor');
    mi('/cms/media', 'cmsMediaTitle');
  }

  // Načtení pořadí menu z backendu
  const { data: menuOrderData } = useSWR<{ order: string[]; isCustom: boolean }>(
    mounted && isAuthenticated ? '/api/user/menu-order' : null
  );

  // Seřadit dropdownItems podle uloženého pořadí
  const orderedDropdownItems = (() => {
    const savedOrder = menuOrderData?.order;
    if (!savedOrder || savedOrder.length === 0) return dropdownItems;
    const orderMap = new Map<string, number>();
    savedOrder.forEach((href, i) => orderMap.set(href, i));
    const ordered: { href: string; label: string; key?: string }[] = [];
    const remaining: { href: string; label: string; key?: string }[] = [];
    for (const item of dropdownItems) {
      if (orderMap.has(item.href)) {
        ordered.push(item);
      } else {
        remaining.push(item);
      }
    }
    ordered.sort((a, b) => (orderMap.get(a.href) ?? 0) - (orderMap.get(b.href) ?? 0));
    return [...ordered, ...remaining];
  })();

  // Přišpendlené položky v pořadí menu
  const pinnedItems = orderedDropdownItems.filter(item => pinnedHrefs.has(item.href));

  // 📌 pin tlačítko — viditelné když je přišpendlené, jinak na hover
  const pinButton = (href: string) => (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(href); }}
      className={`${pinnedHrefs.has(href) ? 'opacity-100 text-moss' : 'opacity-0 group-hover:opacity-100 text-wood/50'} hover:text-moss-dark transition flex-shrink-0 text-xs leading-5`}
      title={pinnedHrefs.has(href) ? t('menuUnpin') : t('menuPin')}
      aria-label={pinnedHrefs.has(href) ? t('menuUnpin') : t('menuPin')}
    >📌</button>
  );

  // Hledání v menu — matchuje aktuální jazyk, angličtinu (ENG fallback) i href.
  // `en` je vždy načtený (staticky importovaný), takže tLocale('en', ...) je synchronní.
  const menuSearchQuery = norm(menuSearch.trim());
  const matchesMenuSearch = (item: { href: string; label: string; key?: string; keywords?: string[] }): boolean => {
    if (!menuSearchQuery) return true;
    if (norm(item.label).includes(menuSearchQuery)) return true;
    if (norm(item.href).includes(menuSearchQuery)) return true;
    if (item.key) {
      const enLabel = norm(tLocale('en', item.key));
      if (enLabel.includes(menuSearchQuery)) return true;
    }
    if (item.keywords) {
      for (const kw of item.keywords) {
        if (norm(kw).includes(menuSearchQuery)) return true;
      }
    }
    return false;
  };

  // Vrátí ENG label, pokud se liší od aktuálního jazyka a dotaz matchuje ENG překlad.
  // Použito pro zobrazení „Faktury (Invoices)" nápovědy ve výsledcích hledání.
  // Ukazuje se i když dotaz matchuje href (např. „invoice" matchuje /obchod/invoices i ENG „Invoices").
  const getEngHint = (item: { href: string; label: string; key?: string }): string | null => {
    if (!menuSearchQuery || !item.key) return null;
    const enLabel = tLocale('en', item.key);
    if (enLabel === item.label) return null;
    if (!norm(enLabel).includes(menuSearchQuery)) return null;
    return enLabel;
  };

  // Filtrované položky pro hledání (jen při aktivním hledání)
  const isMenuSearching = menuSearchOpen && !!menuSearchQuery && !sidebarCollapsed;
  const filteredMenuItems = isMenuSearching
    ? orderedDropdownItems.filter(matchesMenuSearch)
    : orderedDropdownItems;

  // Klávesová navigace ve výsledcích hledání: ↑/↓ pohyb, Enter navigace, Esc zavření
  const handleMenuSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!isMenuSearching || filteredMenuItems.length === 0) {
      if (e.key === 'Escape') { setMenuSearchOpen(false); setMenuSearch(''); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMenuSearchActiveIndex((prev) => Math.min(prev + 1, filteredMenuItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMenuSearchActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredMenuItems[menuSearchActiveIndex];
      if (item) router.push(item.href);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMenuSearchOpen(false);
      setMenuSearch('');
    }
  };

  // Scroll aktivní položky do view při klávesové navigaci
  useEffect(() => {
    if (!isMenuSearching) return;
    const el = menuSearchItemRefs.current[menuSearchActiveIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [menuSearchActiveIndex, isMenuSearching]);

  useEffect(() => {
    const saved = window.localStorage.getItem('sidebarCollapsed');
    if (saved === 'true') setSidebarCollapsed(true);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  useEffect(() => {
    // Na CMS hostu je veřejný web bez přihlášení — nepřesměrovávat na login.
    if (cmsHost) return;
    if (mounted && !isAuthenticated) {
      const path = window.location.pathname;
      if (!isPublicPath(path)) {
        sessionStorage.setItem('redirectAfterLogin', path + window.location.search);
        router.replace('/login');
      }
    }
  }, [cmsHost, mounted, isAuthenticated, router]);

  useEffect(() => {
    if (mounted && isAuthenticated) {
      const path = window.location.pathname;
      if (path === '/login') {
        // Skip if finishLogin() already handled the redirect (e.g. user just
        // logged in and finishLogin called router.push). Otherwise the
        // "already authenticated" effect would override it with router.replace('/').
        if (isLoginRedirectHandled()) {
          setLoginRedirectHandled(false);
          return;
        }
        const redirect = sessionStorage.getItem('redirectAfterLogin');
        if (redirect) {
          sessionStorage.removeItem('redirectAfterLogin');
          router.replace(redirect);
        } else {
          router.replace('/');
        }
      }
    }
  }, [mounted, isAuthenticated, router]);

  useEffect(() => {
    setDropdownOpen(false);
    setCreateMenuOpen(false);
  }, [pathname]);

  // Track last visited internal paths for useReturnPath (unauthorized "Zpět" etc.)
  useEffect(() => {
    if (pathname) recordPathVisit(pathname + window.location.search);
  }, [pathname]);

  const waitCursorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const waitCursorFailsafeRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      if (href.startsWith('/') && !href.startsWith('/api/') && !anchor.hasAttribute('target')) {
        if (waitCursorTimerRef.current) clearTimeout(waitCursorTimerRef.current);
        waitCursorTimerRef.current = setTimeout(() => {
          waitCursorTimerRef.current = undefined;
          document.body.style.cursor = 'wait';
          // Failsafe: navigation to the same pathname never triggers the
          // pathname-change clear — auto-reset so the cursor can't stick.
          if (waitCursorFailsafeRef.current) clearTimeout(waitCursorFailsafeRef.current);
          waitCursorFailsafeRef.current = setTimeout(() => {
            waitCursorFailsafeRef.current = undefined;
            document.body.style.cursor = '';
          }, 5000);
        }, 200);
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      if (waitCursorTimerRef.current) clearTimeout(waitCursorTimerRef.current);
      if (waitCursorFailsafeRef.current) clearTimeout(waitCursorFailsafeRef.current);
    };
  }, []);

  // Clear wait cursor after navigation completes (pathname change).
  // Also cancels a pending timer — without this, a fast client-side
  // navigation (<200ms) lets the timer fire AFTER the clear, leaving
  // the cursor stuck on 'wait' until the next navigation.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (waitCursorTimerRef.current) {
        clearTimeout(waitCursorTimerRef.current);
        waitCursorTimerRef.current = undefined;
      }
      if (waitCursorFailsafeRef.current) {
        clearTimeout(waitCursorFailsafeRef.current);
        waitCursorFailsafeRef.current = undefined;
      }
      document.body.style.cursor = '';
    }
  }, [pathname]);

  // Reset loading overlay when route changes. When navigating away from
  // a page (e.g. order detail → list), the previous page's in-flight SWR
  // requests are still running and hold the overlay. Resetting on route
  // change clears those stale requests so the overlay doesn't get stuck.
  useEffect(() => {
    notifyLoadingReset();
  }, [pathname]);

  // Track menu visits for dynamic menu mode — fire-and-forget, silent.
  useEffect(() => {
    if (!mounted || !isAuthenticated || !pathname || pathname === '/' || pathname === '/login') return;
    apiFetch('/api/user/menu-visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ href: pathname }),
      silent: true,
    }).catch(() => { /* tiché — tracking nesmí blokovat UI */ });
  }, [pathname, mounted, isAuthenticated]);

  // Track last opened agenda in localStorage (shared across tabs/windows).
  // On new tab or browser restart, if URL is "/", redirect to lastPath.
  // Excludes: public pages, /login, /register, /forgot-password, /reset-password,
  // /book/*, /bookings/*, /support/track/*, /public/* (public pages).


  useEffect(() => {
    if (!mounted || !isAuthenticated || !pathname) return;
    if (pathname === '/' || pathname === '/login' || isPublicPath(pathname)) return;
    try {
      window.localStorage.setItem(`lastPath:${window.localStorage.getItem('userId') || 'anon'}`, pathname + window.location.search);
    } catch { /* tiché */ }
  }, [pathname, mounted, isAuthenticated]);

  // On mount: if authenticated and on "/", redirect to lastPath (if any).
  // Also re-run when pathname changes (e.g. after finishLogin redirects to "/",
  // this effect needs to fire to redirect to lastPath).
  // Exception: a deliberate click on the header logo must reach the home page —
  // it sets skipLastPathRedirectRef, which this effect consumes once.
  const skipLastPathRedirectRef = useRef(false);
  useEffect(() => {
    // Na CMS hostu je "/" veřejná homepage — i přihlášený admin ji chce vidět.
    if (cmsHost) return;
    if (!mounted || !isAuthenticated) return;
    if (typeof window === 'undefined') return;
    const currentPath = pathname || window.location.pathname;
    if (currentPath !== '/') return;
    if (skipLastPathRedirectRef.current) {
      skipLastPathRedirectRef.current = false;
      return;
    }
    // Don't redirect if there's a redirect param already (middleware set it)
    const params = new URLSearchParams(window.location.search);
    if (params.has('redirect')) return;
    const uid = window.localStorage.getItem('userId') || 'anon';
    const lastPath = window.localStorage.getItem(`lastPath:${uid}`) || window.localStorage.getItem('lastPath');
    if (lastPath && lastPath !== '/' && !isPublicPath(lastPath)) {
      router.replace(lastPath);
    }
  }, [cmsHost, mounted, isAuthenticated, router, pathname]);

  // Menu usage data for dynamic mode
  const { data: menuUsageData, mutate: mutateMenuUsage } = useSWR<{ href: string; visitCount: number; lastVisited: string }[]>(
    mounted && isAuthenticated && menuMode === 'dynamic' ? '/api/user/menu-usage' : null,
    { refreshInterval: 300_000 }
  );
  // Top 8 most-visited hrefs
  const topMenuHrefs = menuUsageData?.slice(0, 8).map(u => u.href) ?? [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!createMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [createMenuOpen]);

  useEffect(() => {
    if (!menuSearchOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuSearchRef.current && !menuSearchRef.current.contains(e.target as Node)) {
        setMenuSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuSearchOpen]);

  useEffect(() => {
    setMenuSearchOpen(false);
    setMenuSearch('');
    setMenuSearchActiveIndex(0);
  }, [pathname]);

  // Reset aktivního indexu při změně vyhledávacího dotazu + persist do sessionStorage
  useEffect(() => {
    setMenuSearchActiveIndex(0);
    if (menuSearch) {
      try { sessionStorage.setItem('menuSearchQuery', menuSearch); } catch { /* tiché */ }
    } else {
      try { sessionStorage.removeItem('menuSearchQuery'); } catch { /* tiché */ }
    }
  }, [menuSearch]);

  // Obnova dotazu ze sessionStorage při otevření hledání (Ctrl+K / klik)
  const restoreMenuSearch = () => {
    try {
      const saved = sessionStorage.getItem('menuSearchQuery');
      if (saved) { setMenuSearch(saved); }
    } catch { /* tiché */ }
  };

  // Keyboard shortcut: Ctrl+K / Cmd+K to toggle menu search
  // Ctrl+Shift+K / Cmd+Shift+K to open global data search
  useEffect(() => {
    if (!mounted || !isAuthenticated) return;
    const handleKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        if (e.shiftKey) {
          // Globální vyhledávání napříč daty
          e.preventDefault();
          setGlobalSearchOpen(true);
          return;
        }
        e.preventDefault();
        setMenuSearchOpen((prev) => {
          const next = !prev;
          if (next) {
            if (sidebarCollapsed) {
              setSidebarCollapsed(false);
              window.localStorage.setItem('sidebarCollapsed', 'false');
            }
            restoreMenuSearch();
            setTimeout(() => menuSearchInputRef.current?.focus(), 200);
          }
          return next;
        });
      }
      if (e.key === 'Escape' && menuSearchOpen) {
        setMenuSearchOpen(false);
        setMenuSearch('');
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mounted, isAuthenticated, menuSearchOpen, sidebarCollapsed]);

  const onLogout = () => {
    authLogout();  // auth.tsx handles redirect + overlay
    setDropdownOpen(false);
    // No router.push here — auth.tsx does window.location.href = '/login'
    // after awaiting the logout API call. This prevents a double redirect.
  };

  // === Page title (agendaName) — lookup map instead of 170-line if-else ===
  // Exact path matches
  const EXACT_PATH_TITLES: Record<string, string> = {
    '/devices': 'terminals',
    '/add-terminal': 'terminals',
    '/payments': 'payments',
    '/create-payment': 'quickPayment',
    '/payment-requests': 'paymentRequests',
    '/profile': 'profile',
    '/admin/jobs': 'jobsAdmin',
    '/admin': 'adminPanel',
    '/admin/payments': 'adminPayments',
    '/admin/users': 'adminUsers',
    '/credit': 'credit',
    '/calendar': 'calendar',
    '/danovy-kalendar': 'menuTaxCalendar',
    '/tasks': 'tasks',
    '/meetings': 'meetings',
    '/calls': 'calls',
    '/im': 'imMessages',
    '/notes': 'notes',
    '/news': 'newsTitle',
    '/admin/news': 'adminNewsTitle',
    '/email': 'email',
    '/sms': 'menuSms',
    '/admin/email-accounts': 'emailAccounts',
    '/admin/ooo-logs': 'oooLogs',
    '/admin/credit': 'adminCreditOverview',
    '/admin/products': 'adminProducts',
    '/admin/settings': 'defaultSettingsTitle',
    '/admin/cases': 'adminCasesTitle',
    '/admin/saldo': 'adminSaldoTitle',
    '/admin/i18n-modules': 'i18nModulesTitle',
    '/admin/registry': 'adminRegistry',
    '/admin/config': 'defaultSettingsTitle',
    '/admin/system': 'adminSystemTitle',
    '/admin/entities': 'menuEntities',
    '/admin/view-configs': 'menuViewConfigs',
    '/admin/datatable-filters': 'menuDatatableFilters',
    '/admin/cms/pages': 'menuCmsPages',
    '/admin/cms/templates': 'menuCmsTemplates',
    '/admin/cms/blocks': 'menuCmsBlocks',
    '/admin/entity-views': 'menuEntityViews',
    '/admin/home-page': 'widgetTemplatesTitle',
    '/admin/roles': 'adminRolesTitle',
    '/admin/pohoda-connection': 'pohodaConnection',
    '/admin/batch-jobs': 'batchJobs',
    '/admin/menu-order': 'menuOrder',
    '/admin/invoice-methods': 'adminInvoiceMethods',
    '/admin/audit-logs': 'auditLogs',
    '/admin/google-sync': 'googleSyncTitle',
    '/admin/sync-logs': 'adminSyncLogs',
    '/admin/stripe-logs': 'adminStripeLogs',
    '/admin/backend-health': 'adminBackendHealthTitle',
    '/admin/frontend-errors': 'adminFrontendErrors',
    '/admin/notifications': 'notifBlockTitle',
    '/admin/sort-order': 'adminSortOrder',
    '/admin/print-reports': 'adminPrintReports',
    '/admin/units': 'lblMeasurementUnits',
    '/admin/layouts': 'adminLayouts',
    '/admin/email-templates': 'adminEmailTemplates',
    '/admin/pohoda-sync': 'adminPohodaSync',
    '/admin/pohoda-users': 'pohodaUsersTitle',
    '/admin/stredisko-mappings': 'adminStrediskoMappings',
    '/admin/licenses': 'adminLicenses',
    '/menu-order': 'myMenuOrder',
    '/contacts': 'menuContacts',
    '/obchod/products': 'menuCatalog',
    '/obchod/sklad': 'menuSklad',
    '/obchod/price-actions': 'menuPriceActions',
    '/obchod/invoices': 'adminInvoicesPohoda',
    '/obchod/analytics': 'menuSalesAnalytics',
    '/brands/analytics': 'menuBrandAnalytics',
    '/crm': 'menuCrmDashboard',
    '/crm/forecast': 'menuCrmForecast',
    '/crm/funnel': 'menuCrmFunnel',
    '/crm/customer360': 'menuCrmCustomer360',
    '/crm/concept': 'menuCrmConcept',
    '/crm/activities': 'menuCrmActivities',
    '/crm/leads': 'menuCrmLeads',
    '/crm/cases': 'menuCrmCases',
    '/crm/opportunities': 'menuCrmOpportunities',
    '/crm/supervision': 'menuCrmSupervision',
    '/crm/supervision/settings': 'menuImpulseSettings',
    '/crm/contact-sources': 'menuCrmContactSources',
    '/in-chat': 'menuInChat',
    '/documents': 'menuDocuments',
    '/chat': 'menuChat',
    '/links': 'menuLinks',
    '/monitors': 'menuMonitors',
    '/vincis': 'menuVincisAgenda',
    '/help': 'menuNapoveda',
    '/cms/nicov': 'menuWebEditor',
    '/cms/editor': 'menuWebEditor',
    '/cms/media': 'cmsMediaTitle',
    '/login': 'login',
    '/forgot-password': 'login',
    '/reset-password': 'login',
    '/register': 'register',
  };

  // Prefix matches (checked after exact, longest prefix first)
  const PREFIX_PATH_TITLES: [string, string][] = [
    ['/devices/', 'terminals'],
    ['/payments/', 'payments'],
    ['/reservations', 'menuReservations'],
    ['/admin/teams', 'teams'],
    ['/obchod/invoices', 'adminInvoicesPohoda'],
    ['/obchod/orders', 'adminOrdersPohoda'],
    ['/obchod/shipments', 'menuShipments'],
    ['/obchod/exchange-rates', 'menuExchangeRates'],
    ['/datovka', 'menuDatovka'],
    ['/obchod/purchase-orders', 'menuPurchaseOrders'],
    ['/obchod/brands', 'menuBrandsPohoda'],
    ['/email/settings', 'emailSettingsTitle'],
    ['/email/view', 'email'],
    ['/email/attachment', 'email'],
    ['/vincis/faktury', 'vincisFaktury'],
    ['/vincis/adresar', 'vincisAdresar'],
    ['/vincis/vykazy-fakturace', 'vincisVykazy'],
    ['/accounts', 'adminAccountsPohoda'],
    ['/crm/leads/', 'menuCrmLeads'],
    ['/crm/opportunities/', 'menuCrmOpportunities'],
    ['/help', 'menuNapoveda'],
    ['/admin/', 'adminPanel'], // catch-all for /admin/* (must be last admin prefix)
  ];

  // Armorik sub-routes (exact path → i18n key)
  const ARMORIK_TITLES: Record<string, string> = {
    '/armorik/owners': 'menuArmorikOwners',
    '/armorik/periods': 'menuArmorikPeriods',
    '/armorik/vat-rates': 'menuVatRates',
    '/armorik/vat-report': 'menuVatReport',
    '/armorik/chart-of-accounts': 'menuChartOfAccounts',
    '/armorik/coa-templates': 'menuCoaTemplates',
    '/armorik/pre-accounting': 'menuPreAccounting',
    '/armorik/folders': 'menuFolders',
    '/armorik/bank-accounts': 'menuBankAccounts',
    '/armorik/partner-bank-accounts': 'menuPartnerBankAccounts',
    '/armorik/bank-statements': 'menuBankStatements',
    '/armorik/payment-orders': 'poTitle',
    '/armorik/payment-details': 'sectionPaymentDetails',
    '/armorik/number-sequences': 'armorikTileNumberSequences',
    '/armorik/saldo-sheets': 'menuSaldo',
    '/armorik/invoices': 'adminInvoicesPohoda',
    '/armorik/orders': 'adminOrdersPohoda',
    '/armorik/contracts': 'armorikContracts',
    '/armorik/journal': 'armorikJournal',
  };

  // Armorik period sub-pages (last segment → i18n key)
  const ARMORIK_PERIOD_TITLES: Record<string, string> = {
    invoices: 'adminInvoicesPohoda',
    orders: 'adminOrdersPohoda',
    contracts: 'armorikContracts',
    journal: 'armorikJournal',
    folders: 'menuFolders',
    aging: 'sectionAging',
    ledger: 'sectionLedger',
    'pre-accounting': 'sectionPreAccounting',
    'balance-sheet': 'sectionBalanceSheet',
    'profit-loss': 'sectionProfitLoss',
    closing: 'sectionClosing',
    'income-tax': 'incomeTaxTitle',
    'vat-report': 'sectionVatReport',
    'tax-calendar': 'sectionTaxCalendar',
    'tax-evidence': 'armorikPeriodTaxEvidence',
    'fixed-assets': 'sectionFixedAssets',
    'travel-expenses': 'sectionTravelExpenses',
    'cash-desk': 'cashDeskTitle',
  };

  // Payroll sub-routes (exact path → i18n key)
  const PAYROLL_TITLES: Record<string, string> = {
    '/payroll/employees': 'payrollEmployeesTitle',
    '/payroll/periods': 'payrollPeriodsTitle',
    '/payroll/slips': 'payrollSlipsTitle',
    '/payroll/components': 'payrollComponentsTitle',
    '/payroll/absences': 'menuPayrollAbsences',
    '/payroll/employments': 'menuPayrollEmployments',
    '/payroll/deductions': 'menuPayrollDeductions',
    '/payroll/filings': 'menuPayrollFilings',
    '/payroll/hr': 'menuPayrollHr',
    '/payroll/moje': 'menuPayrollMoje',
    '/payroll/config': 'payrollTaxesTitle',
    '/payroll/reports': 'payrollReports',
  };

  let agendaName = '';
  if (mounted) {
    if (EXACT_PATH_TITLES[pathname]) {
      agendaName = t(EXACT_PATH_TITLES[pathname]);
    } else if (pathname === '/armorik' || pathname.startsWith('/armorik/')) {
      if (pathname.startsWith('/armorik/owners/') && pathname.includes('/periods/')) {
        const parts = pathname.split('/').filter(Boolean);
        if (parts.length >= 6) {
          const last = parts[parts.length - 1];
          agendaName = t(ARMORIK_PERIOD_TITLES[last] || 'armorikDashboard');
        } else {
          agendaName = t('armorikSelectPeriod');
        }
      } else if (pathname.startsWith('/armorik/owners/')) {
        agendaName = t('armorikSelectPeriod');
      } else if (pathname.startsWith('/armorik/saldo-sheets/')) {
        agendaName = t('menuSaldo');
      } else {
        // Detail cesty (/armorik/chart-of-accounts/55, …) — prefix match na kolekci
        const armorikKey = ARMORIK_TITLES[pathname]
          || Object.entries(ARMORIK_TITLES).find(([p]) => pathname.startsWith(p + '/'))?.[1];
        agendaName = t(armorikKey || 'menuArmorikSelectOwner');
      }
    } else if (pathname === '/payroll' || pathname.startsWith('/payroll/')) {
      // Check exact payroll sub-routes first
      const payrollKey = Object.keys(PAYROLL_TITLES).find(key =>
        pathname === key || pathname.startsWith(key + '/')
      );
      agendaName = payrollKey ? t(PAYROLL_TITLES[payrollKey]) : t('payrollTitle');
    } else {
      // Check prefix matches
      for (const [prefix, key] of PREFIX_PATH_TITLES) {
        if (pathname.startsWith(prefix)) {
          agendaName = t(key);
          break;
        }
      }
    }
  }

  // Build breadcrumbs from pathname for nested routes
  // i18n keys for breadcrumb segments — no hardcoded Czech
  const BREADCRUMB_KEYS: Record<string, string> = {
    vincis: 'bcVincis',
    pohoda: 'bcPohoda',
    armorik: 'bcArmorik',
    admin: 'bcAdmin',
    crm: 'bcCrm',
    obchod: 'bcObchod',
    faktury: 'bcFaktury',
    adresar: 'bcAdresar',
    'vykazy-fakturace': 'bcVykazyFakturace',
    invoices: 'bcInvoices',
    orders: 'bcOrders',
    products: 'bcProducts',
    accounts: 'bcAccounts',
    brands: 'bcBrands',
    users: 'bcUsers',
    teams: 'bcTeams',
    leads: 'bcLeads',
    opportunities: 'bcOpportunities',
    owners: 'bcOwners',
    periods: 'bcPeriods',
    contracts: 'bcContracts',
    journal: 'bcJournal',
    folders: 'bcFolders',
    'vat-rates': 'bcVatRates',
    'chart-of-accounts': 'bcChartOfAccounts',
    'coa-templates': 'bcCoaTemplates',
    'pre-accounting': 'bcPreAccounting',
    'bank-accounts': 'bcBankAccounts',
    'partner-bank-accounts': 'bcPartnerBankAccounts',
    'bank-statements': 'bcBankStatements',
    'number-sequences': 'armorikTileNumberSequences',
    payroll: 'bcPayroll',
    employees: 'bcEmployees',
    slips: 'bcSlips',
    components: 'bcComponents',
    absences: 'menuPayrollAbsences',
    employments: 'menuPayrollEmployments',
    deductions: 'menuPayrollDeductions',
    filings: 'menuPayrollFilings',
    hr: 'menuPayrollHr',
    moje: 'menuPayrollMoje',
    config: 'bcConfig',
    reports: 'bcReports',
    'purchase-orders': 'bcPurchaseOrders',
    'google-sync': 'bcGoogleSync',
    'entity-views': 'bcEntityViews',
    entities: 'bcEntities',
    'audit-logs': 'bcAuditLogs',
    'sync-logs': 'bcSyncLogs',
    'pohoda-connection': 'bcPohodaConnection',
    'batch-jobs': 'bcBatchJobs',
    'menu-order': 'bcMenuOrder',
    roles: 'bcRoles',
    'print-reports': 'bcPrintReports',
    'email-accounts': 'bcEmailAccounts',
    'ooo-logs': 'bcOooLogs',
    'home-page': 'bcHomePage',
    reservations: 'bcReservations',
    help: 'menuNapoveda',
    email: 'email',
    settings: 'emailSettingsTitle',
  };

  const breadcrumbs: { label: string; href: string }[] = [];
  const bcSegsFull = pathname.split('/').filter(Boolean);
  const lastSegIdx = bcSegsFull.length - 1;
  const resolvedLastCrumb = lastSegIdx > 0 && /^\d+$/.test(bcSegsFull[lastSegIdx] || '') ? bcResolved?.[lastSegIdx] : undefined;
  // True when the last path segment is a collection name (e.g. /obchod/orders) —
  // inline-detail case. False when it's a record id (path detail), where the
  // agendaName crumb must not be rendered again before the record name.
  const lastSegIsCollection = !!BREADCRUMB_KEYS[bcSegsFull[lastSegIdx] || ''];
  if (mounted && agendaName) {
    const segments = pathname.split('/').filter(Boolean);
    // For 2+ segments, show parent path as clickable breadcrumbs
    if (segments.length >= 2) {
      for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        const i18nKey = BREADCRUMB_KEYS[seg];
        let label = i18nKey ? (t(i18nKey) || seg) : (seg.charAt(0).toUpperCase() + seg.slice(1));
        if (bcResolved?.[i]) label = bcResolved[i];
        const href = '/' + segments.slice(0, i + 1).join('/');
        breadcrumbs.push({ label, href });
      }
    }
    // Payroll agenda patří pod účetní jednotku — vložit její název jako crumb
    if (segments[0] === 'payroll' && segments.length >= 2 && payrollUnitId) {
      const unit = payrollOwners?.find(o => o.id === payrollUnitId);
      if (unit) breadcrumbs.splice(1, 0, { label: unit.name, href: `/payroll?ownerId=${unit.id}` });
    }
  }

  // Event modály (úkol/schůzka/hovor/pozn.) — agenda prefix zkrátit na první písmeno
  const eventSeg = pathname.split('/').filter(Boolean)[0] || '';
  const titleAgenda = recordNameOverride && ['tasks', 'meetings', 'calls', 'im', 'notes', 'calendar'].includes(eventSeg)
    ? (agendaName?.charAt(0) ?? '')
    : agendaName;
  const expectedTitle = titleAgenda && recordNameOverride
    ? `${titleAgenda}: ${recordNameOverride} — ${siteName}`
    : agendaName
      ? `${agendaName} — ${siteName}`
      : siteName;

  useEffect(() => {
    // Don't override title on public support pages (they have their own metadata)
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/support')) return;
    document.title = expectedTitle;

    // Next.js App Router metadata system can re-apply the <title> tag from the
    // root layout's static metadata when a Suspense boundary resolves (e.g. on
    // /email where EmailPageInner suspends via useSearchParams). That overwrites
    // our manually-set document.title because this effect's deps don't change.
    // Watch for external title changes and re-apply ours.
    const titleEl = document.querySelector('title');
    if (!titleEl) return;
    const observer = new MutationObserver(() => {
      if (document.title !== expectedTitle) {
        document.title = expectedTitle;
      }
    });
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [expectedTitle]);

  const isPublicPage = pathname === '/nicov' || pathname === '/book' || pathname.startsWith('/book/') || pathname === '/bookings' || pathname.startsWith('/bookings/') || pathname === '/support' || pathname.startsWith('/support/') || pathname.startsWith('/public/') || pathname === '/shop' || pathname.startsWith('/shop/');

  // Auth pages where sidebar/header-auth elements should NOT render even if
  // isAuthenticated is already true (prevents sidebar flashing before navigation
  // from /login to / completes after finishLogin sets the auth state).
  const isAuthPage = pathname === '/login' || pathname === '/register' || pathname === '/forgot-password' || pathname === '/reset-password';
  const showAuthedUI = mounted && isAuthenticated && !isAuthPage;

  // Na CMS hostu (veřejný web) návštěvník bez přihlášení vidí jen stránku
  // — žádné app chrome. Přihlášený admin vidí plnou aplikaci.
  if (isPublicPage || (cmsHost && !showAuthedUI)) {
    return (
      <>
        <LoadingBar />
        {children}
      </>
    );
  }

  return (
    <EventModalProvider>
    <HelpDrawerProvider>
    <div className="min-h-screen bg-cream text-ink font-sans">
      <ImpersonationBanner />
      <header className="sticky top-0 z-40 border-b border-wheat bg-linen">
        <div className="w-full px-4 py-2 flex items-center gap-2">
          {showAuthedUI && (
            <button
              data-no-loading
              onClick={() => setMobileSidebarOpen(true)}
              title={t('openMenu')}
              aria-label={t('openMenu')}
              className="md:hidden text-wood hover:text-moss-dark text-2xl px-1 py-0.5 rounded hover:bg-cream transition flex-shrink-0"
            >
              ☰
            </button>
          )}
          <div className="flex items-center gap-2 flex-shrink-0 max-w-[40%] overflow-hidden">
            <Link href="/" onClick={() => { if (pathname !== '/') { skipLastPathRedirectRef.current = true; try { const u = window.localStorage.getItem('userId') || 'anon'; window.localStorage.removeItem(`lastPath:${u}`); } catch {} } }} className="text-xl font-semibold text-bark hover:text-moss-dark transition whitespace-nowrap">🌾 {siteName}</Link>
            {agendaName && (
              <>
                {breadcrumbs.map((bc, idx) => (
                  <span key={bc.href} className="flex items-center gap-1 whitespace-nowrap">
                    <span className="text-wheat">/</span>
                    <Link href={bc.href} className="text-sm text-wood hover:text-moss-dark transition truncate max-w-[8rem]" title={bc.label}>
                      {bc.label}
                    </Link>
                  </span>
                ))}
                {/* When an inline detail is open (recordNameOverride without an id path segment),
                    show agendaName as a clickable level before the record name */}
                {recordNameOverride && !resolvedLastCrumb && lastSegIsCollection && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span className="text-wheat">/</span>
                    <Link href={pathname} className="text-sm text-wood hover:text-moss-dark transition truncate max-w-[8rem]" title={agendaName}>
                      {agendaName}
                    </Link>
                  </span>
                )}
                {(recordNameOverride || resolvedLastCrumb || (lastSegIsCollection ? agendaName : null)) && (
                  <>
                    <span className="text-wheat">/</span>
                    <span className="text-sm text-bark whitespace-nowrap">
                      {recordNameOverride || resolvedLastCrumb || agendaName}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          <nav className="flex items-center gap-3 ml-auto flex-shrink-0">

            {/* Globální vyhledávání — pouze přihlášená zóna */}
            {showAuthedUI && (
              <HeaderSearch onOpenModal={() => setGlobalSearchOpen(true)} />
            )}

            {/* Jazykový přepínač — pouze nepřihlášená zóna (nebo auth stránky) */}
            {mounted && (!isAuthenticated || isAuthPage) && (
              <select
                id="nav-language-switcher"
                value={locale}
                onChange={(e) => changeLocale(e.target.value as Locale)}
                className="text-sm bg-linen border border-wheat text-wood rounded-rustic px-2 py-1 cursor-pointer hover:border-moss transition focus:outline-none focus:ring-1 focus:ring-moss"
                aria-label={t('languageLabel')}
                title={t('languageLabel')}
              >
                {getOrderedLanguages().map(([code, { flag }]) => (
                  <option key={code} value={code}>
                    {flag} {code.toUpperCase()}
                  </option>
                ))}
              </select>
            )}

            {mounted && !isAuthPage && visibleNavItems.map((item) => {
              const active = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={
                    active
                      ? 'text-moss-dark font-semibold border-b-2 border-moss pb-0.5'
                      : 'text-wood hover:text-moss-dark transition'
                  }
                >
                  {item.label}
                </Link>
              );
            })}
            {/* Kontextová nápověda — jen když článek pro aktuální stránku existuje; otevře postranní panel */}
            {showAuthedUI && helpSlugs.has(helpSlugForPath(pathname)) && (
              <HeaderHelpButton slug={helpSlugForPath(pathname)} />
            )}
            {/* Theme toggle — authenticated zone */}
            {showAuthedUI && (
              <button
                type="button"
                onClick={toggleTheme}
                className="p-1 text-lg hover:opacity-80 transition focus:outline-none"
                title={theme === 'dark' ? (t('lightTheme')) : (t('darkTheme'))}
                aria-label={theme === 'dark' ? (t('lightTheme')) : (t('darkTheme'))}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            )}
            {/* Font size selector — authenticated zone */}
            {showAuthedUI && (
              <div ref={fontSizeRef} className="relative hidden sm:block">
                <button
                  type="button"
                  onClick={() => setFontSizeOpen(v => !v)}
                  className="text-lg hover:opacity-80 transition focus:outline-none"
                  title={t('fontSize')}
                  aria-label={t('fontSize')}
                >
                  🔤
                </button>
                {fontSizeOpen && (
                  <div className="absolute right-0 top-full mt-2 z-50 bg-cream dark:bg-linen border border-wheat dark:border-wheat rounded-lg shadow-xl p-3 min-w-[280px]">
                    <div className="text-xs text-wood dark:text-wood mb-2">{t('fontSize')}</div>
                    <div className="flex gap-1.5 items-center flex-wrap">
                      {(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'] as FontSize[]).map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => { setFontSize(size); }}
                          title={`${FONT_SIZE_PX[size]}px`}
                          style={{ fontSize: `${FONT_SIZE_PX[size] - 4}px` }}
                          className={`w-7 h-7 rounded-full border-2 transition flex items-center justify-center shrink-0 font-bold ${
                            fontSize === size
                              ? 'bg-moss border-moss text-cream'
                              : 'bg-cream dark:bg-cream hover:bg-wheat/50 border-wheat dark:border-wheat text-wood dark:text-wood'
                          }`}
                        >
                          A
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Theme toggle — unauthenticated zone (login/register pages) */}
            {mounted && !isAuthenticated && (
              <button
                type="button"
                onClick={toggleTheme}
                className="text-lg hover:opacity-80 transition focus:outline-none"
                title={theme === 'dark' ? (t('lightTheme')) : (t('darkTheme'))}
                aria-label={theme === 'dark' ? (t('lightTheme')) : (t('darkTheme'))}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            )}
            {/* Odkaz do nastavení profilu — pouze přihlášená zóna (modal, aby uživatel neztratil aktuální stránku) */}
            {showAuthedUI && (
              <button
                type="button"
                onClick={() => setProfileModalOpen(true)}
                className="p-1 text-lg hover:opacity-80 transition focus:outline-none"
                title={t('profile')}
                aria-label={t('profile')}
              >
                {LANGUAGES[locale]?.flag || '🌐'}
              </button>
            )}

            {/* Create event + menu */}
            {showAuthedUI && (role === 'ADMIN' || permissions.includes('calendar.view') || permissions.includes('armorik.accounting.edit')) && (
              <div className="relative" ref={createMenuRef}>
                <button
                  type="button"
                  onClick={() => setCreateMenuOpen((o) => !o)}
                  className="flex items-center justify-center w-8 h-8 rounded-rustic bg-moss text-linen hover:bg-moss-dark transition focus:outline-none"
                  aria-label={t('add')}
                  title={t('add')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                {createMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-rustic border border-wheat bg-linen shadow-lg py-1 z-50">
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/tasks?new=true', label: t('createMenuTask') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">✅</span>
                      {t('createMenuTask')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/meetings?new=true', label: t('createMenuMeeting') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">👥</span>
                      {t('createMenuMeeting')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/calls?new=true', label: t('createMenuCall') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">📞</span>
                      {t('createMenuCall')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); void composeEmail({}); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">✉️</span>
                      {t('createMenuEmail')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/crm/leads?new=true', label: t('addLead') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">🚀</span>
                      {t('addLead')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/crm/opportunities?new=true', label: t('addOpportunity') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">💡</span>
                      {t('addOpportunity')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/contacts?new=1', label: t('newContact') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">📇</span>
                      {t('newContact')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/accounts?new=true', label: t('newAccount') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">🏢</span>
                      {t('newAccount')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/obchod/orders?new=true', label: t('newOrder') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">📜</span>
                      {t('newOrder')}
                    </button>
                    <button
                      onClick={() => { setCreateMenuOpen(false); setOpenChoice({ href: '/crm/cases?new=true', label: t('newCase') }); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bark hover:bg-cream transition text-left"
                    >
                      <span className="w-5 text-center">📋</span>
                      {t('newCase')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Notification bell */}
            {showAuthedUI && (
              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={() => setNotifOpen((o) => !o)}
                  className="relative text-wood hover:text-moss-dark transition focus:outline-none"
                  aria-label={t('notifications')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount && unreadCount.count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rust text-linen text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unreadCount.count > 99 ? '99+' : unreadCount.count}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-80 rounded-rustic border border-wheat bg-linen shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-wheat sticky top-0 bg-linen">
                      <span className="text-sm font-semibold text-bark">{t('notifications')}</span>
                      {unreadCount && unreadCount.count > 0 && (
                        <button
                          type="button"
                          onClick={markAllAsRead}
                          className="text-xs text-moss hover:text-moss-dark transition"
                        >
                          {t('markAllRead')}
                        </button>
                      )}
                    </div>
                    {notifications && notifications.length > 0 ? (
                      notifications.map((n) => {
                        const kind = classifyNotif(n.type);
                        const { label: typeLabel, icon: typeIcon, openLabel } = notifDisplayInfo(kind, n.type, t, n.eventType, n.priority);
                        const openHref = notifOpenHref(kind, n.refId);
                        return (
                          <div
                            key={n.id}
                            className={`px-4 py-3 border-b border-wheat/50 transition ${!n.read ? 'bg-moss/5' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-moss flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="text-xs">{typeIcon}</span>
                                  <span className="text-xs font-semibold text-moss-dark uppercase tracking-wide">{typeLabel}</span>
                                  {n.createdAt && (
                                    <span className="text-xs text-wood/60 ml-auto">
                                      {new Date(n.createdAt).toLocaleString(fmtLocale)}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-bark truncate">{n.title}</p>
                                {n.body && <p className="text-xs text-wood mt-0.5 truncate">{n.body}</p>}
                                <div className="flex items-center gap-2 mt-2">
                                  {n.refId && openHref && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!n.read) markAsRead(n.id);
                                        setNotifOpen(false);
                                        router.push(openHref);
                                      }}
                                      className="text-xs font-semibold text-moss hover:text-moss-dark transition inline-flex items-center gap-1"
                                    >
                                      {openLabel}
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!n.read) markAsRead(n.id);
                                    }}
                                    className="text-xs text-wood hover:text-bark transition ml-auto"
                                  >
                                    {t('notifDismiss')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-8 text-center text-sm text-wood">
                        {t('noNotifications')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {showAuthedUI && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  data-no-loading
                  onClick={() => setDropdownOpen((open) => !open)}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-moss text-linen font-semibold text-sm hover:bg-moss-dark transition focus:outline-none ring-2 ring-wheat/50"
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                  aria-label={t('menuMenu')}
                >
                  {(() => {
                    const fn = (firstName || '').trim();
                    const ln = (lastName || '').trim();
                    const initials = (fn.charAt(0) + ln.charAt(0)).toUpperCase() || (email || 'U').charAt(0).toUpperCase();
                    return initials;
                  })()}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-rustic border border-wheat bg-linen shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-wheat">
                      <p className="text-sm font-medium text-bark truncate">
                        {(firstName || lastName) ? `${firstName ?? ''} ${lastName ?? ''}`.trim() : email || 'User'}
                      </p>
                      {role && <p className="text-xs text-wood mt-0.5 capitalize">{role}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setDropdownOpen(false); setProfileModalOpen(true); }}
                      className="flex items-center justify-between w-full px-4 py-2 text-sm text-wood hover:bg-cream hover:text-moss-dark transition"
                    >
                      <span>{t('tabSettings')}</span>
                      <span
                        className="text-wood/50 hover:text-moss-dark transition"
                        title={t('openInNewTab')}
                        aria-label={`${t('openInNewTab')}: ${t('tabSettings')}`}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open('/profile', '_blank', 'noopener,noreferrer'); setDropdownOpen(false); }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </span>
                    </button>
                    {mounted && role === 'ADMIN' && (
                      <a
                        href="/admin"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-2 text-sm text-wood hover:bg-cream hover:text-moss-dark transition"
                      >
                        <span>{t('adminPanel')}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open('/admin', '_blank', 'noopener,noreferrer'); setDropdownOpen(false); }}
                          className="text-wood/50 hover:text-moss-dark transition"
                          title={t('openInNewTab')}
                          aria-label={`${t('openInNewTab')}: ${t('adminPanel')}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </a>
                    )}
                    <a
                      href="/menu-order"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center justify-between px-4 py-2 text-sm text-wood hover:bg-cream hover:text-moss-dark transition"
                    >
                      <span>{t('myMenuOrder')}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open('/menu-order', '_blank', 'noopener,noreferrer'); setDropdownOpen(false); }}
                        className="text-wood/50 hover:text-moss-dark transition"
                        title={t('openInNewTab')}
                        aria-label={t('openMenuOrderInNewTab')}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </a>
                    <button
                      type="button"
                      onClick={onLogout}
                      className="w-full text-left px-4 py-2 text-sm text-wood hover:bg-cream hover:text-moss-dark transition"
                    >
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </header>
      <div className="flex">
        {/* Levé boční menu — pod ikonou květiny.
            Skryté na auth stránkách i když je isAuthenticated, aby se sidebar
            neobjevil dřív než navigace z login stránky dokončí. */}
        {showAuthedUI && (
          <>
          {/* Mobile sidebar overlay */}
          {(mobileSidebarOpen || mobileSidebarExiting) && (
            <div
              className={`fixed inset-0 z-30 bg-bark/40 backdrop-blur-sm md:hidden ${mobileSidebarOpen ? 'animate-backdropIn' : 'animate-backdropOut'}`}
              onClick={() => setMobileSidebarOpen(false)}
              aria-hidden="true"
            />
          )}
          <aside
            className={`${sidebarCollapsed ? 'w-14 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-wheat/60 [&::-webkit-scrollbar-track]:bg-transparent' : 'w-56'} ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:sticky top-14 left-0 z-40 md:z-auto border-r border-wheat bg-linen self-start transition-all duration-200 flex flex-col`}
            style={{ maxHeight: 'calc(100dvh - 56px)' }}
            onMouseOver={(e) => {
              const a = (e.target as HTMLElement).closest?.('a[href]');
              if (!a) return;
              try { prefetchRoute(new URL((a as HTMLAnchorElement).href).pathname); } catch { /* ignore */ }
            }}
          >
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-end'} px-1 py-0 border-b border-wheat/50 shrink-0`}>
              {!sidebarCollapsed && (
                <div className="flex items-center flex-1 min-w-0 mr-1 gap-0.5">
                  <input
                    ref={menuSearchInputRef}
                    type="text"
                    name="menuSearch"
                    value={menuSearch}
                    onChange={(e) => { setMenuSearch(e.target.value); setMenuSearchOpen(true); }}
                    onFocus={() => setMenuSearchOpen(true)}
                    onKeyDown={handleMenuSearchKeyDown}
                    placeholder={t('searchMenu')}
                    className="flex-1 min-w-0 text-sm rounded-rustic border border-wheat bg-cream px-2 py-1 text-bark focus:outline-none focus:ring-1 focus:ring-moss"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  {menuSearch && (
                    <button
                      type="button"
                      data-no-loading
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={() => { setMenuSearch(''); menuSearchInputRef.current?.focus(); }}
                      title={t('clearInput')}
                      aria-label={t('clearInput')}
                      className="shrink-0 text-wood hover:text-rust w-8 h-8 flex items-center justify-center rounded transition"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              {!sidebarCollapsed && (
                <button
                  data-no-loading
                  onClick={cycleMenuMode}
                  title={menuModeTitle}
                  className="shrink-0 text-wood hover:text-moss-dark text-base px-1.5 py-0.5 rounded hover:bg-cream transition flex-shrink-0"
                >
                  {menuModeIcon}
                </button>
              )}
              <button
                data-no-loading
                onClick={() => {
                  setMenuSearchOpen(o => !o);
                  if (!menuSearchOpen) {
                    if (sidebarCollapsed) {
                      setSidebarCollapsed(false);
                      window.localStorage.setItem('sidebarCollapsed', 'false');
                    }
                    restoreMenuSearch();
                    setTimeout(() => menuSearchInputRef.current?.focus(), sidebarCollapsed ? 200 : 0);
                  }
                }}
                title={`${t('searchMenu')} (Ctrl+K)`}
                onMouseEnter={(e) => { if (sidebarCollapsed) { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: t('searchMenu'), top: rect.top + rect.height / 2 }); } }}
                onMouseLeave={() => hideSidebarTooltip()}
                className={`group relative text-wood hover:text-moss-dark text-lg px-1.5 py-0.5 rounded hover:bg-cream transition z-[9999] flex-shrink-0 ${!sidebarCollapsed ? 'hidden' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <button
                data-no-loading
                onClick={toggleSidebar}
                title={sidebarCollapsed ? t('expandMenu') : t('collapseMenu')}
                onMouseEnter={(e) => { if (sidebarCollapsed) { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: t('expandMenu'), top: rect.top + rect.height / 2 }); } }}
                onMouseLeave={() => hideSidebarTooltip()}
                className="group relative text-wood hover:text-moss-dark text-2xl px-2 py-0.5 rounded hover:bg-cream transition z-[9999] flex-shrink-0"
              >
                {sidebarCollapsed ? '»' : '«'}
              </button>
            </div>
            <nav className="py-1 overflow-y-auto flex-1 min-h-0" ref={menuSearchRef}>
              {/* Přišpendlené položky — vždy nahoře, ve všech režimech */}
              {pinnedItems.length > 0 && !isMenuSearching && (
                <div className="mb-1 border-b border-wheat/50 pb-1">
                  {!sidebarCollapsed && (
                    <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ochre flex items-center gap-1">
                      <span>📌</span>
                      <span>{t('menuPinned')}</span>
                    </div>
                  )}
                  {pinnedItems.map((item) => {
                    const active = pathname === item.href;
                    const isInChat = item.href === '/in-chat';
                    const isChat = item.href === '/chat';
                    const chatUnread = isChat ? (chatUnreadData?.unreadCount ?? 0) : isInChat ? (inChatUnread?.unreadCount ?? 0) : (item.href === '/datovka' ? databoxUnread : item.href === '/reservations' ? reservationsPending : 0);
                    const icon = MENU_ICONS[item.href] || '📄';
                    return sidebarCollapsed ? (
                      <div key={`pin-${item.href}`} className={`group relative flex items-stretch border-l-2 ${active ? 'border-moss bg-cream' : 'border-transparent'}`}>
                        <Link
                          href={item.href}
                          title={item.label}
                          onClick={() => setMobileSidebarOpen(false)}
                          onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: item.label, top: rect.top + rect.height / 2, href: item.href }); }}
                          onMouseLeave={() => hideSidebarTooltip()}
                          className={`flex items-center justify-center flex-1 pl-1 pr-2 py-2 text-base ${active ? 'text-moss-dark font-semibold' : 'text-wood hover:bg-cream hover:text-moss-dark transition'}`}
                        >
                          {icon}
                        </Link>
                        {chatUnread > 0 && (
                          <span className="absolute top-0 right-0 bg-rust text-linen text-[10px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 pointer-events-none">
                            {chatUnread > 9 ? '9+' : chatUnread}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={`pin-${item.href}`}
                        href={item.href}
                        className={`group relative flex items-center justify-between px-4 py-2 text-sm ${active
                            ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                            : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                        }`}
                      >
                        <span className="flex items-start gap-2 min-w-0">
                          <span className="text-base flex-shrink-0 leading-5">{icon}</span>
                          <span className="truncate">{item.label}</span>
                        </span>
                        {chatUnread > 0 && (
                          <span className="bg-rust text-linen text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                            {chatUnread > 99 ? '99+' : chatUnread}
                          </span>
                        )}
                        {pinButton(item.href)}
                      </Link>
                    );
                  })}
                </div>
              )}
              {/* Sections mode: vertikální sbalitelné sekce.
                  Při hledání nebo collapsed sidebar se chová jako flat (všechny položky). */}
              {(menuMode === 'sections' || menuMode === 'dynamic') && !sidebarCollapsed && !isMenuSearching ? (
                <>
                  {/* Dynamic mode: t('menuFrequent') sekce nahoře */}
                  {menuMode === 'dynamic' && topMenuHrefs.length > 0 && (() => {
                    // Match usage hrefs to menu items by prefix:
                    // /admin/google-sync → /admin, /obchod/orders/81 → /obchod/orders
                    const topItems = orderedDropdownItems.filter(item =>
                      topMenuHrefs.some(usageHref =>
                        usageHref === item.href || usageHref.startsWith(item.href + '/')
                      )
                    );
                    if (topItems.length === 0) return null;
                    return (
                      <div className="mb-1 border-b border-wheat/50 pb-1">
                        <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ochre flex items-center gap-1">
                          <span>★</span>
                          <span>{t('menuFrequent')}</span>
                        </div>
                        {topItems.map((item) => {
                          const active = pathname === item.href;
                          const isInChat = item.href === '/in-chat';
                          const isChat = item.href === '/chat';
                          const chatUnread = isChat ? (chatUnreadData?.unreadCount ?? 0) : isInChat ? (inChatUnread?.unreadCount ?? 0) : (item.href === '/datovka' ? databoxUnread : item.href === '/reservations' ? reservationsPending : 0);
                          const icon = MENU_ICONS[item.href] || '📄';
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              className={`group relative flex items-center justify-between px-4 py-2 text-sm ${active
                                  ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                                  : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                              }`}
                            >
                              <span className="flex items-start gap-2 min-w-0">
                                <span className="text-base flex-shrink-0 leading-5">{icon}</span>
                                <span className="truncate">{item.label}</span>
                              </span>
                              {chatUnread > 0 && (
                                <span className="bg-rust text-linen text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                  {chatUnread > 99 ? '99+' : chatUnread}
                                </span>
                              )}
                              {pinButton(item.href)}
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(item.href, '_blank', 'noopener,noreferrer'); }}
                                className="opacity-0 group-hover:opacity-100 text-wood/50 hover:text-moss-dark transition flex-shrink-0"
                                title={t('openInNewTab')}
                                aria-label={`${t('openInNewTab')}: ${item.label}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </button>
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {MENU_SECTIONS.filter(section => {
                    return orderedDropdownItems.some(item => findSectionId(item.href) === section.id);
                  }).map(section => {
                    const label = t(section.labelKey) || section.id;
                    const isCollapsed = collapsedSections.has(section.id);
                    const sectionItems = filteredMenuItems.filter(item => findSectionId(item.href) === section.id);
                    // Auto-expand sekce která obsahuje aktuální stránku
                    const hasActiveItem = sectionItems.some(item => pathname === item.href);
                    const showItems = !isCollapsed || hasActiveItem;
                    return (
                      <div key={section.id} className="mb-0.5">
                        <button
                          data-no-loading
                          onClick={() => toggleSection(section.id)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition rounded ${
                            hasActiveItem
                              ? 'text-moss-dark bg-cream/60'
                              : 'text-wood/70 hover:text-moss-dark hover:bg-cream/30'
                          }`}
                        >
                          <span className="text-sm">{section.icon}</span>
                          <span className="flex-1 text-left truncate">{label}</span>
                          <span className={`text-wood/40 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>▶</span>
                        </button>
                        {showItems && sectionItems.map((item) => {
                          const active = pathname === item.href;
                          const isInChat = item.href === '/in-chat';
                          const isChat = item.href === '/chat';
                          const chatUnread = isChat ? (chatUnreadData?.unreadCount ?? 0) : isInChat ? (inChatUnread?.unreadCount ?? 0) : (item.href === '/datovka' ? databoxUnread : item.href === '/reservations' ? reservationsPending : 0);
                          const icon = MENU_ICONS[item.href] || '📄';
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              className={`group relative flex items-center justify-between pl-8 pr-4 py-2 text-sm ${active
                                  ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                                  : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                              }`}
                            >
                              <span className="flex items-start gap-2 min-w-0">
                                <span className="text-base flex-shrink-0 leading-5">{icon}</span>
                                <span className="truncate">{item.label}</span>
                              </span>
                              {chatUnread > 0 && (
                                <span className="bg-rust text-linen text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                  {chatUnread > 99 ? '99+' : chatUnread}
                                </span>
                              )}
                              {pinButton(item.href)}
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(item.href, '_blank', 'noopener,noreferrer'); }}
                                className="opacity-0 group-hover:opacity-100 text-wood/50 hover:text-moss-dark transition flex-shrink-0"
                                title={t('openInNewTab')}
                                aria-label={`${t('openInNewTab')}: ${item.label}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </button>
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })}
                  {/* t('invOther') sekce — položky bez přiřazení (uložené filtry) */}
                  {(() => {
                    const otherItems = filteredMenuItems.filter(item => findSectionId(item.href) === 'other');
                    if (otherItems.length === 0) return null;
                    const isCollapsed = collapsedSections.has('other');
                    const hasActiveItem = otherItems.some(item => pathname === item.href);
                    const showItems = !isCollapsed || hasActiveItem;
                    return (
                      <div className="mb-0.5">
                        <button
                          data-no-loading
                          onClick={() => toggleSection('other')}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition rounded ${
                            hasActiveItem
                              ? 'text-moss-dark bg-cream/60'
                              : 'text-wood/70 hover:text-moss-dark hover:bg-cream/30'
                          }`}
                        >
                          <span className="text-sm">☰</span>
                          <span className="flex-1 text-left truncate">{t('menuOther')}</span>
                          <span className={`text-wood/40 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>▶</span>
                        </button>
                        {showItems && otherItems.map((item) => {
                          const active = pathname === item.href;
                          const icon = MENU_ICONS[item.href] || '📄';
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              className={`group relative flex items-center justify-between pl-8 pr-4 py-2 text-sm ${active
                                  ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                                  : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                              }`}
                            >
                              <span className="flex items-start gap-2 min-w-0">
                                <span className="text-base flex-shrink-0 leading-5">{icon}</span>
                                <span className="truncate">{item.label}</span>
                              </span>
                              {pinButton(item.href)}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* Pořadí mého menu — vždy poslední položka */}
                  {(() => {
                    const moHref = '/menu-order';
                    const moActive = pathname === moHref;
                    const moIcon = MENU_ICONS[moHref] || '⚙️';
                    return (
                      <Link
                        href={moHref}
                        className={`group relative flex items-center justify-between px-4 py-2 text-sm ${moActive
                            ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                            : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="text-base flex-shrink-0">{moIcon}</span>
                          <span className="truncate">{t('menuOrderTitle')}</span>
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(moHref, '_blank', 'noopener,noreferrer'); }}
                          className="opacity-0 group-hover:opacity-100 text-wood/50 hover:text-moss-dark transition flex-shrink-0"
                          title={t('openInNewTab')}
                          aria-label={t('openMenuOrderInNewTab')}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </Link>
                    );
                  })()}
                </>
              ) : (
                <>
              {/* Collapsed sidebar: zobraz t('menuFrequent') nahoře s oddělovačem */}
              {sidebarCollapsed && menuMode === 'dynamic' && topMenuHrefs.length > 0 && (() => {
                const topItems = orderedDropdownItems.filter(item =>
                  topMenuHrefs.some(usageHref =>
                    usageHref === item.href || usageHref.startsWith(item.href + '/')
                  )
                );
                if (topItems.length === 0) return null;
                return (
                  <>
                    {topItems.map((item) => {
                      const active = pathname === item.href;
                      const isInChat = item.href === '/in-chat';
                      const isChat = item.href === '/chat';
                      const chatUnread = isChat ? (chatUnreadData?.unreadCount ?? 0) : isInChat ? (inChatUnread?.unreadCount ?? 0) : (item.href === '/datovka' ? databoxUnread : item.href === '/reservations' ? reservationsPending : 0);
                      const icon = MENU_ICONS[item.href] || '📄';
                      return (
                        <div key={`top-${item.href}`} className={`group relative flex items-stretch border-l-2 ${active ? 'border-moss bg-cream' : 'border-transparent'}`}>
                          <Link
                            href={item.href}
                            title={item.label}
                            onClick={() => setMobileSidebarOpen(false)}
                            onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: item.label, top: rect.top + rect.height / 2, href: item.href }); }}
                            onMouseLeave={() => hideSidebarTooltip()}
                            className={`flex items-center justify-center flex-1 pl-1 pr-2 py-2 text-base ${active ? 'text-moss-dark font-semibold' : 'text-wood hover:bg-cream hover:text-moss-dark transition'}`}
                          >
                            {icon}
                          </Link>
                          {chatUnread > 0 && (
                            <span className="absolute top-0 right-0 bg-rust text-linen text-[10px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 pointer-events-none">
                              {chatUnread > 9 ? '9+' : chatUnread}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div className="my-1 border-b border-wheat/40" />
                  </>
                );
              })()}
              {filteredMenuItems.map((item, index) => {
                const active = pathname === item.href;
                const isInChat = item.href === '/in-chat';
                const isChat = item.href === '/chat';
                const chatUnread = isChat ? (chatUnreadData?.unreadCount ?? 0) : isInChat ? (inChatUnread?.unreadCount ?? 0) : (item.href === '/datovka' ? databoxUnread : item.href === '/reservations' ? reservationsPending : 0);
                const icon = MENU_ICONS[item.href] || '📄';
                const isSearching = isMenuSearching;
                const isActiveSearchItem = isSearching && index === menuSearchActiveIndex;
                return sidebarCollapsed ? (
                  <div key={item.href} className={`group relative flex items-stretch border-l-2 ${active ? 'border-moss bg-cream' : 'border-transparent'}`}>
                    <Link
                      href={item.href}
                      title={item.label}
                      onClick={() => setMobileSidebarOpen(false)}
                      onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: item.label, top: rect.top + rect.height / 2, href: item.href }); }}
                      onMouseLeave={() => hideSidebarTooltip()}
                      className={`flex items-center justify-center flex-1 pl-1 pr-2 py-2 text-base ${active ? 'text-moss-dark font-semibold' : 'text-wood hover:bg-cream hover:text-moss-dark transition'}`}
                    >
                      {icon}
                    </Link>
                    {chatUnread > 0 && (
                      <span className="absolute top-0 right-0 bg-rust text-linen text-[10px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 pointer-events-none">
                        {chatUnread > 9 ? '9+' : chatUnread}
                      </span>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    ref={(el) => { menuSearchItemRefs.current[index] = el; }}
                    href={item.href}
                    onMouseEnter={() => { if (isSearching) setMenuSearchActiveIndex(index); }}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm ${active
                        ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                        : isActiveSearchItem
                        ? 'bg-moss/15 text-moss-dark font-medium border-l-2 border-moss/50'
                        : isSearching
                        ? 'bg-moss/5 text-wood hover:bg-moss/10 hover:text-moss-dark transition border-l-2 border-moss/30'
                        : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                    }`}
                  >
                    <span className="flex items-start gap-2 min-w-0">
                      <span className="text-base flex-shrink-0 leading-5">{icon}</span>
                      <span className="flex flex-col min-w-0">
                        <span className="truncate">{item.label}</span>
                        {isSearching && (() => { const engHint = getEngHint(item); return engHint ? <span className="text-xs text-wood/50 italic truncate">({engHint})</span> : null; })()}
                      </span>
                    </span>
                    {chatUnread > 0 && (
                      <span className="bg-rust text-linen text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {chatUnread > 99 ? '99+' : chatUnread}
                      </span>
                    )}
                    {pinButton(item.href)}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(item.href, '_blank', 'noopener,noreferrer'); }}
                      className="opacity-0 group-hover:opacity-100 text-wood/50 hover:text-moss-dark transition flex-shrink-0"
                      title={t('openInNewTab')}
                      aria-label={`${t('openInNewTab')}: ${item.label}`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </Link>
                );
              })}
              {isMenuSearching && filteredMenuItems.length === 0 && (
                <div className="px-4 py-3 text-sm text-wood text-center">
                  {t('msgNoResults')}
                </div>
              )}
              {/* Pořadí mého menu — vždy poslední položka (skryto při hledání) */}
              {!isMenuSearching && (() => {
                const moHref = '/menu-order';
                const moActive = pathname === moHref;
                const moIcon = MENU_ICONS[moHref] || '⚙️';
                return sidebarCollapsed ? (
                  <div key={moHref} className={`group relative flex items-stretch border-l-2 ${moActive ? 'border-moss bg-cream' : 'border-transparent'}`}>
                    <Link
                      href={moHref}
                      title={t('menuOrderTitle')}
                      onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); showSidebarTooltip({ label: t('menuOrderTitle'), top: rect.top + rect.height / 2, href: moHref }); }}
                      onMouseLeave={() => hideSidebarTooltip()}
                      className={`flex items-center justify-center flex-1 pl-1 pr-2 py-2 text-base ${moActive ? 'text-moss-dark font-semibold' : 'text-wood hover:bg-cream hover:text-moss-dark transition'}`}
                    >
                      {moIcon}
                    </Link>
                  </div>
                ) : (
                  <Link
                    key={moHref}
                    href={moHref}
                    className={`group relative flex items-center justify-between px-4 py-2 text-sm ${moActive
                        ? 'bg-cream text-moss-dark font-semibold border-l-2 border-moss'
                        : 'text-wood hover:bg-cream hover:text-moss-dark transition border-l-2 border-transparent'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-base flex-shrink-0">{moIcon}</span>
                      <span className="truncate">{t('menuOrderTitle')}</span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(moHref, '_blank', 'noopener,noreferrer'); }}
                      className="opacity-0 group-hover:opacity-100 text-wood/50 hover:text-moss-dark transition flex-shrink-0"
                      title={t('openInNewTab')}
                      aria-label={t('openMenuOrderInNewTab')}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </Link>
                );
              })()}
                </>
              )}
            </nav>
          </aside>
          </>
        )}
        {sidebarTooltip && sidebarCollapsed && (
          <div
            className="fixed whitespace-nowrap rounded-md bg-bark px-3 py-2 text-sm text-cream shadow-lg z-[99999]"
            style={{ left: '3.5rem', top: sidebarTooltip.top, transform: 'translateY(-50%)' }}
            onMouseEnter={() => { if (tooltipHideTimer.current) { clearTimeout(tooltipHideTimer.current); tooltipHideTimer.current = null; } }}
            onMouseLeave={() => hideSidebarTooltip()}
          >
            <div className="flex items-center gap-3">
              <span>{sidebarTooltip.label}</span>
              {sidebarTooltip.href && (
                <button
                  type="button"
                  onClick={() => { window.open(sidebarTooltip.href, '_blank', 'noopener,noreferrer'); setSidebarTooltip(null); }}
                  className="text-cream/60 hover:text-cream underline text-sm"
                >
                  {t('openInNewTab')}
                </button>
              )}
            </div>
          </div>
        )}
        <main className="relative flex-1 min-w-0 px-4 py-1 text-left pb-16 md:pb-1">
          <LoadingBar scoped />
          <ErrorBoundary label={t('layoutPageContent')}>
            {children}
          </ErrorBoundary>
        </main>
      </div>
      <BackendHealth active={mounted} />
      {/* Mobilní bottom nav — Domů + přišpendlené + Menu */}
      {showAuthedUI && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-linen/95 backdrop-blur-sm border-t border-wheat flex items-stretch" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {[
            { href: '/', icon: '🏠', label: t('navHome') },
            ...pinnedItems.slice(0, 3).map(i => ({ href: i.href, icon: MENU_ICONS[i.href] || '📄', label: i.label })),
          ].map(item => (
            <Link key={item.href} href={item.href}
              className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] min-w-0 ${pathname === item.href ? 'text-moss font-semibold' : 'text-wood'}`}>
              <span className="text-lg leading-none">{item.icon}</span>
              <span className="truncate max-w-full px-1">{item.label}</span>
            </Link>
          ))}
          <button
            data-no-loading
            onClick={() => setMobileSidebarOpen(true)}
            className="flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] text-wood"
            aria-label={t('openMenu')}
          >
            <span className="text-lg leading-none">☰</span>
            <span>{t('menuMenu')}</span>
          </button>
        </nav>
      )}

      <footer className="px-4 py-3 pb-20 md:pb-3 text-sm text-wood/80 bg-cream/95 backdrop-blur-sm border-t border-wheat">
        <div className="relative flex flex-col md:flex-row items-center gap-0.5 md:gap-0">
          <span className="hidden md:block flex-1" />
          <span className="md:absolute md:left-1/2 md:-translate-x-1/2 text-center">
            {(() => {
              const ft = settings?.footer_text?.trim();
              if (ft) return ft.replace('{year}', String(new Date().getFullYear()));
              return mounted && `${siteName} - ${(() => { const variants = t('slogan').split('|').map(s => s.trim()).filter(Boolean); return variants[sloganVariant % variants.length] ?? variants[0]; })()}`;
            })()}
          </span>
          <span className="md:flex-1 text-center md:text-right text-[10px] md:text-xs" suppressHydrationWarning>
            {!cmsHost && (
              <>
                {t('version')} FE {APP_VERSION}
                {buildDateLabel && ` • ${buildDateLabel}`}
                {backendInfo?.build?.version && ` | BE ${backendInfo.build.version}`}
                {backendInfo?.build?.time && ` • ${new Date(backendInfo.build.time).toLocaleString(getFormatLocale())}`}
              </>
            )}
          </span>
        </div>
      </footer>

      {(idleWarning || idleWarningExiting) && (
        <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-bark/50 backdrop-blur-sm ${idleWarning ? 'animate-backdropIn' : 'animate-backdropOut'}`}>
          <div role="dialog" aria-modal="true" className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 max-w-sm w-full mx-4 text-center ${idleWarning ? 'animate-modalIn' : 'animate-modalOut'}`}>
            <div className="text-4xl mb-3">⏱️</div>
            <h2 className="text-lg font-semibold text-bark mb-2">{t('idleWarningTitle')}</h2>
            <p className="text-sm text-wood mb-4">
              {t('idleWarningText')}{' '}
              <span className="font-bold text-rust">{idleSecondsLeft}s</span>.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                className="btn-primary"
                onClick={dismissIdleWarning}
              >
                {t('stayLoggedIn')}
              </button>
              <button
                className="btn-secondary"
                onClick={onLogout}
              >
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout overlay — unified LoadingOverlay (ripple rings = odchod) */}
      <LoadingOverlay
        variant="logout"
        label={t('loggingOut')}
        visible={isLoggingOut}
      />

      {/* Notification banners — stacked */}
      {bannerNotifs.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[9998] flex flex-col gap-3 max-w-md">
          {bannerNotifs.filter(n => !snoozedNotifs.has(n.id)).map((notif) => {
            const progress = getProgressToEvent(notif.eventTime, notif.createdAt);
            const isOverdue = notif.eventTime ? new Date(notif.eventTime).getTime() < Date.now() : false;
            const borderClass = isOverdue ? 'border-rust/50' : getPriorityBorder(notif.priority);
            const kind = classifyNotif(notif.type);
            const { label: typeLabel, icon: typeIcon, openLabel } = notifDisplayInfo(kind, notif.type, t, notif.eventType, notif.priority);
            const openHref = notifOpenHref(kind, notif.refId);
            const isCaseReply = kind === 'case_reply';
            const isCalendar = kind === 'calendar';
            return (
            <div key={notif.id} className={`bg-linen border-2 ${borderClass} rounded-rustic shadow-2xl p-5 flex items-start gap-3 animate-slide-in`}>
              <div className="text-2xl flex-shrink-0 mt-0.5">{typeIcon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-xs font-semibold text-moss-dark uppercase tracking-wide">{typeLabel}</span>
                  {notif.createdAt && (
                    <span className="text-xs text-wood/60 ml-auto">
                      {new Date(notif.createdAt).toLocaleString(fmtLocale)}
                    </span>
                  )}
                </div>
                <a
                  href={notif.refId && openHref ? openHref : '#'}
                  className="text-base font-semibold text-moss-dark hover:text-moss underline truncate block"
                  onClick={(e) => {
                    e.preventDefault();
                    if (notif.refId && openHref) {
                      router.push(openHref);
                    }
                    if (!notif.read) markAsRead(notif.id);
                    dismissBanner(notif.id);
                  }}
                >
                  {notif.title}
                </a>
                {notif.body && !notif.eventTime && (
                  <p className="text-sm text-wood mt-0.5 line-clamp-2">{notif.body}</p>
                )}
                {notif.eventTime && (
                  <p className={`text-xs font-medium mt-0.5 ${isOverdue ? 'text-rust' : 'text-wood'}`}>
                    {isOverdue ? '⚠ ' : '🕐 '}{formatEventDateTime(notif.eventTime, t)}
                    {notif.eventEndTime && ` – ${formatEventDateTime(notif.eventEndTime, t)}`}
                    {notif.location ? ` • ${notif.location}` : ''}
                  </p>
                )}
                {notif.dueDate && (
                  <p className={`text-xs font-medium mt-0.5 ${isOverdue ? 'text-rust' : 'text-wood'}`}>
                    ⏳ {t('deadline')}: {formatEventDateTime(notif.dueDate, t)}
                  </p>
                )}
                {progress !== null && (
                  <div className="mt-2 w-full h-1 bg-bark/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${isOverdue ? 'bg-rust' : 'bg-moss'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {notif.refId && openHref && (
                    <button
                      className="text-sm px-3 py-1 rounded border border-moss bg-moss text-linen hover:bg-moss-dark transition font-medium inline-flex items-center gap-1"
                      onClick={() => {
                        router.push(openHref);
                        if (!notif.read) markAsRead(notif.id);
                        dismissBanner(notif.id);
                      }}
                    >
                      {openLabel}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </button>
                  )}
                  {!isCaseReply && isCalendar && (
                    <button
                      className="text-sm px-3 py-1 rounded border border-moss bg-moss text-linen hover:bg-moss-dark transition font-medium"
                      onClick={() => completeAndDismiss(notif.id, notif.refId)}
                    >
                      ✓ {t('completed')}
                    </button>
                  )}
                  <button
                    className="text-sm px-3 py-1 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition"
                    onClick={() => readAndDismiss(notif.id)}
                  >
                    {t('notifDismiss')}
                  </button>
                </div>
                {isCalendar && (
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className="text-xs text-wood/60">{t('snooze')}:</span>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => snoozeNotif(notif, 3)}>3 min</button>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => snoozeNotif(notif, 5)}>5 min</button>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => snoozeNotif(notif, 15)}>15 min</button>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => snoozeNotif(notif, 30)}>30 min</button>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => snoozeNotif(notif, 60)}>1 h</button>
                  <button className="text-xs px-2 py-0.5 rounded border border-wheat bg-cream text-wood hover:bg-wheat/30 transition" onClick={() => {
                    const tomorrow9 = new Date();
                    tomorrow9.setDate(tomorrow9.getDate() + 1);
                    tomorrow9.setHours(9, 0, 0, 0);
                    const mins = Math.round((tomorrow9.getTime() - Date.now()) / 60000);
                    snoozeNotif(notif, mins);
                  }}>{t('tomorrowMorning')}</button>
                  <input
                    type="text"
                    inputMode="numeric"
                    min={1}
                    max={1440}
                    placeholder="…"
                    className="w-12 text-xs px-1 py-0.5 border border-wheat bg-cream text-wood rounded"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = parseInt((e.target as HTMLInputElement).value, 10);
                        if (val > 0 && val <= 1440) {
                          snoozeNotif(notif, val);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }
                    }}
                  />
                  <span className="text-xs text-wood/40">min</span>
                </div>
                )}
              </div>
              <button
                className="text-wood/50 hover:text-bark flex-shrink-0 text-xl"
                onClick={() => dismissBanner(notif.id)}
              >✕</button>
            </div>
          );})}
        </div>
      )}

      {/* Globální vyhledávání napříč daty (Ctrl+Shift+K) */}
      <GlobalSearchModal open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />

      {/* Modální okno nastavení profilu (z horní lišty — uživatel neztratí aktuální stránku) */}
      <ProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} initialTab="settings" />

      {/* Dialog volby otevření — zde vs nový tab vs zrušit */}
      {(openChoice || openChoiceExiting) && choice && (
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/40 backdrop-blur-sm ${openChoice ? 'animate-backdropIn' : 'animate-backdropOut'}`}
          onClick={(e) => { if (e.target === e.currentTarget) setOpenChoice(null); }}
        >
          <div role="dialog" aria-modal="true" className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 w-full max-w-sm mx-4 ${openChoice ? 'animate-modalIn' : 'animate-modalOut'}`}>
            <h3 className="text-lg font-semibold text-bark mb-2">{choice.label}</h3>
            <p className="text-sm text-wood mb-6">{t('openChoiceQuestion')}</p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { router.push(choice.href); setOpenChoice(null); }}
                className="w-full py-2.5 px-4 rounded-rustic text-sm font-semibold bg-moss text-linen hover:bg-moss-dark transition"
              >
                📄 {t('openChoiceHere')}
              </button>
              <button
                type="button"
                onClick={() => { window.open(choice.href, '_blank', 'noopener,noreferrer'); setOpenChoice(null); }}
                className="w-full py-2.5 px-4 rounded-rustic text-sm font-semibold bg-bark/10 text-bark hover:bg-bark/20 transition border border-wheat"
              >
                🪟 {t('openChoiceNewTab')}
              </button>
              <button
                type="button"
                onClick={() => setOpenChoice(null)}
                className="w-full py-2 px-4 rounded-rustic text-sm text-wood hover:bg-cream transition"
              >
                {t('openChoiceCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </HelpDrawerProvider>
    </EventModalProvider>
  );
}
